import { useState, useEffect, useRef, Component, Fragment } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import { useAuthCtx } from '../App';
import api from '../utils/api';
import { formatDate, statusBadge, capitalize } from '../utils/helpers';
import { useCurrency } from '../hooks/useCurrency';
import { toast } from 'react-toastify';
import MarketingPage from '../components/admin/MarketingPage';
import CouponsPage from '../components/admin/CouponsPage';
import AnalyticsPage from '../components/admin/AnalyticsPage';
import BackupExport from '../components/admin/BackupExport';
import ForecastPage from '../components/admin/ForecastPage';
import PriceSchedulerPage from '../components/admin/PriceSchedulerPage';
import SupportInbox from '../components/admin/SupportInbox';
import TicketsPage from '../components/admin/TicketsPage';
import Icon from '../components/admin/Icon';

// ─── Section error boundary ───────────────────────────────────────────────────
class SectionErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  reset() { this.setState({ crashed: false }); }
  render() {
    if (this.state.crashed) {
      return (
        <div className="py-20 text-center">
          <Icon name="error" className="h-10 w-10 mx-auto text-gray-300 mb-4" />
          <div className="font-bold text-gray-700 text-lg mb-2">Something went wrong in this section</div>
          <div className="text-gray-400 text-sm mb-6">Other sections are unaffected. Try reloading this one.</div>
          <button onClick={() => this.reset()} className="px-4 py-2 bg-tea-600 text-white rounded-lg text-sm font-medium hover:bg-tea-700">
            Reload section
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function BoundedRoute({ element }) {
  return <SectionErrorBoundary>{element}</SectionErrorBoundary>;
}

// ─── Dashboard building blocks ────────────────────────────────────────────────

// Compact trend chip: ▲ +12%  /  ▼ -4%  vs a previous value.
function Trend({ current, previous, suffix = '' }) {
  if (previous == null) return null;
  if (previous === 0) {
    if (!current) return <span className="text-[11px] text-gray-400">—</span>;
    return <span className="text-[11px] font-semibold text-blue-600">▲ new</span>;
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return <span className="text-[11px] text-gray-400">▬ 0%{suffix}</span>;
  const up = pct > 0;
  return (
    <span className={'text-[11px] font-semibold ' + (up ? 'text-blue-600' : 'text-rose-500')}>
      {up ? '▲' : '▼'} {up ? '+' : ''}{pct}%{suffix}
    </span>
  );
}

// Small KPI tile with optional accent + trend.
function Tile({ icon, label, value, sub, trend, accent = 'slate' }) {
  const ring = {
    slate: 'bg-white border-gray-200',
    emerald: 'bg-blue-50 border-blue-200',
    indigo: 'bg-indigo-50 border-indigo-200',
    amber: 'bg-amber-50 border-amber-200',
    rose: 'bg-rose-50 border-rose-200',
    sky: 'bg-sky-50 border-sky-200',
  }[accent] || 'bg-white border-gray-200';
  return (
    <div className={'rounded-xl border p-4 shadow-sm flex flex-col gap-1 ' + ring}>
      <div className="flex items-center justify-between">
        <Icon name={icon} className="h-6 w-6 text-gray-500" />
        {trend}
      </div>
      <div className="text-2xl font-extrabold text-gray-900 leading-tight tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}

// Horizontal meter: a labelled value bar filling toward a max.
function Meter({ label, value, max, display, color = 'bg-tea-500', hint }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium text-gray-600 truncate">{label}</span>
        <span className="font-bold text-gray-800 tabular-nums">{display}</span>
      </div>
      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={'h-full rounded-full transition-all duration-700 ' + color} style={{ width: pct + '%' }} />
      </div>
      {hint && <div className="text-[10px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

// Circular goal gauge (SVG) — today vs a target, 0–100%+.
function Gauge({ pct, label, center, color = '#10b981' }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const r = 42, c = 2 * Math.PI * r;
  const off = c - (clamped / 100) * c;
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#eef2f5" strokeWidth="10" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      </svg>
      <div className="-mt-16 mb-6 text-center">
        <div className="text-lg font-extrabold text-gray-900 tabular-nums">{center}</div>
      </div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold text-center">{label}</div>
    </div>
  );
}

// 14-day revenue sparkline (inline SVG bars).
function Sparkline({ series, fmt }) {
  if (!series || series.length === 0) return <div className="text-xs text-gray-400">No revenue yet.</div>;
  const max = Math.max(...series.map(d => d.revenueCents), 1);
  return (
    <div className="flex items-end gap-1 h-24">
      {series.map((d) => {
        const h = Math.max(3, Math.round((d.revenueCents / max) * 100));
        return (
          <div key={d.day} className="flex-1 group relative flex flex-col justify-end items-center">
            <div className="w-full bg-tea-400 hover:bg-tea-600 rounded-t transition-colors" style={{ height: h + '%' }} />
            <div className="absolute -top-9 hidden group-hover:block bg-gray-900 text-white text-[10px] rounded px-1.5 py-1 whitespace-nowrap z-10">
              {d.day.slice(5)}: {fmt(d.revenueCents)} · {d.orders} ord
            </div>
          </div>
        );
      })}
    </div>
  );
}

// A ranked leaderboard card.
function Leaderboard({ title, icon, rows, render, empty }) {
  return (
    <div className="bg-white rounded-xl border p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Icon name={icon} className="h-5 w-5 text-tea-600" />
        <span className="text-sm font-bold text-gray-700">{title}</span>
      </div>
      {(!rows || rows.length === 0) ? (
        <div className="text-xs text-gray-400 py-4 text-center">{empty}</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className={'w-5 h-5 shrink-0 rounded-full text-[10px] font-bold flex items-center justify-center ' +
                (i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-100 text-gray-500')}>
                {i + 1}
              </span>
              {render(r)}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

const SECTION_KEYS = ['today', 'vip', 'meters', 'trend', 'leaderboards', 'operations'];

// ─── Overview metrics ─────────────────────────────────────────────────────────
function MetricsOverview() {
  const { fmt } = useCurrency({ usd: true });
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [auto, setAuto] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [show, setShow] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('adminOverviewSections') || '{}');
      return SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: saved[k] !== false }), {});
    } catch { return SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {}); }
  });
  const timer = useRef(null);

  const toggle = (k) => setShow(prev => {
    const next = { ...prev, [k]: !prev[k] };
    try { localStorage.setItem('adminOverviewSections', JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  const load = (quiet = false) => {
    if (!quiet) { setLoading(true); setLoadError(false); }
    api.get('/admin/dashboard')
      .then(res => { setData(res.data); setUpdatedAt(new Date()); setLoadError(false); })
      .catch(() => { if (!quiet) { toast.error('Failed to load dashboard'); setLoadError(true); } })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (auto) {
      timer.current = setInterval(() => load(true), 20000);
      return () => clearInterval(timer.current);
    }
  }, [auto]);

  if (loading) {
    return <div className="py-12 text-center text-gray-400">Loading dashboard…</div>;
  }
  if (loadError || !data) {
    return (
      <div className="py-12 text-center">
        <Icon name="alert" className="h-9 w-9 mx-auto text-gray-300 mb-3" />
        <div className="text-gray-600 font-medium mb-4">Could not load dashboard</div>
        <button onClick={() => load()} className="px-4 py-2 bg-tea-600 text-white rounded-lg text-sm font-medium hover:bg-tea-700">
          Retry
        </button>
      </div>
    );
  }

  const { today, totals, statusBreakdown, revenueSeries, topSpenders, topCoupons, topProducts, lowStock, live, vip } = data;

  // Daily average revenue over the last 30d → today's "goal".
  const dailyAvg = totals.orders30d > 0 ? Math.round(totals.revenue30dCents / 30) : 0;
  const goalPct = dailyAvg > 0 ? Math.round((today.revenueCents / dailyAvg) * 100) : (today.revenueCents > 0 ? 100 : 0);
  const subPct = totals.revenueCents > 0 ? Math.round((totals.mrrCents * 12 / (totals.revenueCents || 1)) * 100) : 0;
  const statusMax = Math.max(1, ...statusBreakdown.map(s => s.count));
  const STATUS_COLOR = {
    delivered: 'bg-blue-500', shipped: 'bg-sky-500', processing: 'bg-indigo-500',
    paid: 'bg-tea-500', pending: 'bg-amber-400', cancelled: 'bg-gray-400', refunded: 'bg-rose-400',
  };
  const VIP_STATUS_COLOR = {
    active: 'bg-tea-500', cancelled: 'bg-amber-400', pending: 'bg-indigo-400',
    expired: 'bg-gray-400', past_due: 'bg-rose-400',
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          Dashboard
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
            </span>
            {live} live now
          </span>
        </h1>
        <div className="flex items-center gap-2">
          {updatedAt && <span className="text-[11px] text-gray-400">Updated {updatedAt.toLocaleTimeString()}</span>}
          <button
            onClick={() => setAuto(a => !a)}
            className={'text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ' +
              (auto ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400')}>
            <span className="inline-flex items-center gap-1.5"><Icon name="refresh" className="h-3.5 w-3.5" /> Auto-refresh {auto ? 'on' : 'off'}</span>
          </button>
          <button onClick={() => load()} className="text-xs font-semibold rounded-full px-3 py-1.5 border bg-white text-gray-600 border-gray-300 hover:border-gray-400">
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Section toggles */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          ['today', "Today's pulse", 'pin'], ['vip', 'VIP members', 'members'], ['meters', 'Meters', 'gauge'], ['trend', 'Trend', 'trend'],
          ['leaderboards', 'Leaderboards', 'star'], ['operations', 'Operations', 'tools'],
        ].map(([k, label, ic]) => (
          <button key={k} onClick={() => toggle(k)}
            className={'inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ' +
              (show[k] ? 'bg-tea-600 text-white border-tea-600' : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300')}>
            <Icon name={ic} className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Today's pulse */}
      {show.today && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-7">
          <Tile icon="revenue" label="Revenue today" value={fmt(today.revenueCents)} accent="emerald"
            trend={<Trend current={today.revenueCents} previous={today.prevRevenueCents} />}
            sub="vs yesterday" />
          <Tile icon="orders" label="Orders today" value={today.orders} accent="sky"
            trend={<Trend current={today.orders} previous={today.prevOrders} />} sub="vs yesterday" />
          <Tile icon="views" label="Views today" value={today.views.toLocaleString()} accent="indigo"
            trend={<Trend current={today.views} previous={today.prevViews} />}
            sub={`${today.visitors.toLocaleString()} unique`} />
          <Tile icon="newCustomer" label="New customers" value={today.newCustomers} accent="amber" sub="signed up today" />
          <Tile icon="live" label="Live visitors" value={live} accent="emerald" sub="on the site now" />
          <Tile icon="receipt" label="Avg order value" value={fmt(totals.aovCents)} accent="slate" sub="all time" />
        </div>
      )}

      {/* VIP members — states & income */}
      {show.vip && vip && (
        <div className="mb-7">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
            <Tile icon="members" label="Active VIPs" value={vip.activeMembers} accent="emerald"
              sub={`${vip.activeAnnual} annual · ${vip.activeMonthly} monthly`} />
            <Tile icon="newCustomer" label="New VIPs today" value={vip.newMembersToday} accent="sky" sub="joined today" />
            <Tile icon="star" label="Pending" value={vip.pendingMembers} accent="amber" sub="awaiting first charge" />
            <Tile icon="revenue" label="VIP recurring" value={fmt(vip.mrrCents)} accent="indigo" sub="MRR (normalized)" />
            <Tile icon="gauge" label="Annual run-rate" value={fmt(vip.annualRunRateCents)} accent="emerald" sub="MRR × 12" />
            <Tile icon="chart" label="VIP billed" value={fmt(vip.billedRevenueCents)} accent="slate" sub="lifetime" />
          </div>
          <div className="bg-white rounded-xl border p-5 shadow-sm">
            <span className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-1.5"><Icon name="members" className="h-4 w-4 text-tea-600" /> Membership states</span>
            {(!vip.byStatus || vip.byStatus.length === 0) ? (
              <div className="text-xs text-gray-400 py-4 text-center">No memberships yet.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
                {vip.byStatus.slice().sort((a, b) => b.count - a.count).map(s => (
                  <Meter key={s.status} label={capitalize(s.status.replace('_', ' '))} value={s.count}
                    max={Math.max(1, ...vip.byStatus.map(x => x.count))} display={s.count}
                    color={VIP_STATUS_COLOR[s.status] || 'bg-gray-400'} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Meters & gauges */}
      {show.meters && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-7">
          <div className="bg-white rounded-xl border p-5 shadow-sm flex items-center justify-around">
            <Gauge pct={goalPct} label="Today vs daily avg" center={goalPct + '%'}
              color={goalPct >= 100 ? '#10b981' : goalPct >= 60 ? '#f59e0b' : '#f43f5e'} />
            <Gauge pct={Math.min(100, subPct)} label="Recurring share" center={subPct + '%'} color="#6366f1" />
          </div>
          <div className="bg-white rounded-xl border p-5 shadow-sm lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            <Meter label="Today's revenue → daily avg goal" value={today.revenueCents} max={Math.max(dailyAvg, today.revenueCents)}
              display={fmt(today.revenueCents)} color="bg-blue-500"
              hint={`Daily avg (30d): ${fmt(dailyAvg)}`} />
            <Meter label="MRR (annualized)" value={totals.mrrCents * 12} max={Math.max(totals.revenueCents, totals.mrrCents * 12, 1)}
              display={fmt(totals.mrrCents) + '/mo'} color="bg-indigo-500"
              hint={`${totals.activeSubscriptions} active subscriptions`} />
          </div>
        </div>
      )}

      {/* 14-day trend + order status funnel */}
      {show.trend && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-7">
          <div className="bg-white rounded-xl border p-5 shadow-sm lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-gray-700 flex items-center gap-1.5"><Icon name="trend" className="h-4 w-4 text-tea-600" /> Revenue — last 14 days</span>
              <span className="text-xs text-gray-400">{fmt(totals.revenue30dCents)} in 30d · {totals.orders30d} orders</span>
            </div>
            <Sparkline series={revenueSeries} fmt={fmt} />
          </div>
          <div className="bg-white rounded-xl border p-5 shadow-sm">
            <span className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-1.5"><Icon name="barChart" className="h-4 w-4 text-tea-600" /> Orders by status</span>
            <div className="space-y-2.5">
              {statusBreakdown.sort((a, b) => b.count - a.count).map(s => (
                <Meter key={s.status} label={capitalize(s.status)} value={s.count} max={statusMax}
                  display={s.count} color={STATUS_COLOR[s.status] || 'bg-gray-400'} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Leaderboards */}
      {show.leaderboards && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-7">
          <Leaderboard title="Biggest spenders" icon="gem" rows={topSpenders} empty="No orders yet."
            render={(r) => (
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{r.name}</div>
                  <div className="text-[11px] text-gray-400">{r.orders} orders</div>
                </div>
                <span className="text-sm font-bold text-blue-600 tabular-nums whitespace-nowrap">{fmt(r.spentCents)}</span>
              </div>
            )} />
          <Leaderboard title="Best coupons" icon="ticket" rows={topCoupons} empty="No coupons redeemed."
            render={(r) => (
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-mono font-semibold text-gray-800 truncate">{r.code}</div>
                  <div className="text-[11px] text-gray-400">{r.uses} uses · {fmt(r.discountCents)} off</div>
                </div>
                <span className="text-sm font-bold text-amber-600 tabular-nums whitespace-nowrap">{fmt(r.revenueCents)}</span>
              </div>
            )} />
          <Leaderboard title="Top products" icon="fire" rows={topProducts} empty="No sales yet."
            render={(r) => (
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{r.name}</div>
                  <div className="text-[11px] text-gray-400">{r.units} units</div>
                </div>
                <span className="text-sm font-bold text-tea-600 tabular-nums whitespace-nowrap">{fmt(r.revenueCents)}</span>
              </div>
            )} />
        </div>
      )}

      {/* Operations */}
      {show.operations && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white rounded-xl border p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-gray-700 flex items-center gap-1.5"><Icon name="lowStock" className="h-4 w-4 text-tea-600" /> Low stock alerts</span>
              <span className="text-[11px] text-gray-400">≤ 10 units in stock</span>
            </div>
            {(!lowStock || lowStock.length === 0) ? (
              <div className="text-xs text-gray-400 py-4 text-center inline-flex items-center gap-1.5 w-full justify-center"><Icon name="check" className="h-3.5 w-3.5 text-blue-500" /> All variants well stocked.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {lowStock.map((p, i) => (
                  <div key={i} className={'flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ' +
                    (p.inventory <= 0 ? 'bg-rose-50 border-rose-200' : p.inventory <= 3 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200')}>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-gray-800 truncate">{p.name}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{p.sku}</div>
                    </div>
                    <span className={'text-sm font-bold tabular-nums ' + (p.inventory <= 0 ? 'text-rose-600' : 'text-amber-600')}>
                      {p.inventory <= 0 ? 'OUT' : p.inventory}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border p-5 shadow-sm flex flex-col gap-3">
            <span className="text-sm font-bold text-gray-700 flex items-center gap-1.5"><Icon name="chart" className="h-4 w-4 text-tea-600" /> Lifetime totals</span>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Total revenue</span>
              <span className="font-bold text-gray-900 tabular-nums">{fmt(totals.revenueCents)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Total orders</span>
              <span className="font-bold text-gray-900 tabular-nums">{totals.orders.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Active subscriptions</span>
              <span className="font-bold text-gray-900 tabular-nums">{totals.activeSubscriptions}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Monthly recurring</span>
              <span className="font-bold text-blue-600 tabular-nums">{fmt(totals.mrrCents)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Orders management ────────────────────────────────────────────────────────
function AdminOrderChatModal({ order, onClose }) {
  const { fmt } = useCurrency({ usd: true });
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${order.id}/messages`);
      setMessages(data.messages || []);
    } catch (err) {
      // silent
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/messages`, { body: text });
      setMessages(m => [...m, data]);
      setDraft('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Live chat</div>
            <div className="font-bold text-gray-900">
              {order.customer?.firstName} {order.customer?.lastName}
              <span className="text-gray-400 font-mono text-xs ml-2">#{order.id.slice(0, 8).toUpperCase()}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {capitalize(order.paymentMethod)} · {fmt(order.totalInCents)} · {capitalize(order.status)}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        <div ref={scrollerRef} className="flex-1 overflow-y-auto bg-gray-50 p-5 space-y-3">
          {messages.length === 0 && (
            <div className="text-center text-gray-400 text-sm italic py-10">
              No messages yet. Send the first reply below.
            </div>
          )}
          {messages.map(m => {
            const fromAdmin = m.senderType === 'admin';
            return (
              <div key={m.id} className={'flex ' + (fromAdmin ? 'justify-end' : 'justify-start')}>
                <div className={
                  'max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ' +
                  (fromAdmin
                    ? 'bg-tea-600 text-white rounded-br-sm'
                    : m.senderType === 'system'
                      ? 'bg-gray-200 text-gray-600 italic'
                      : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm')
                }>
                  <div className={'text-[10px] uppercase tracking-wider mb-1 ' + (fromAdmin ? 'text-tea-100' : 'text-gray-400')}>
                    {fromAdmin ? (m.author?.firstName || 'You') : (m.senderType === 'customer' ? 'Customer' : 'System')}
                    <span className="ml-2 opacity-80">{new Date(m.createdAt).toLocaleTimeString()}</span>
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  {m.isPaymentClaim && (
                    <div className={'mt-1 text-[10px] uppercase tracking-wider font-bold ' + (fromAdmin ? 'text-amber-200' : 'text-amber-600')}>
                      ✦ Payment confirmation
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t bg-white p-4 flex items-end gap-2">
          <textarea
            rows={2}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Reply to the customer…"
            className="flex-1 resize-none rounded-lg border border-gray-300 focus:border-tea-500 focus:ring-1 focus:ring-tea-200 focus:outline-none text-sm px-3 py-2"
            maxLength={2000}
          />
          <button
            onClick={send}
            disabled={!draft.trim() || sending}
            className="shrink-0 px-5 py-2.5 rounded-lg bg-tea-600 text-white text-sm font-semibold hover:bg-tea-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Floating live-chat dock (visible across the whole admin area) ────────────
// Polls the unified threads feed: orders with a live customer and/or an unread
// customer message. Surfaces toasts when someone new needs attention, and opens
// the existing chat modal so the admin can reply from anywhere in admin.
function AdminChatDock() {
  const { fmt } = useCurrency({ usd: true });
  const [threads, setThreads] = useState([]);
  const [counts, setCounts] = useState({ unreadCount: 0, liveCount: 0 });
  const [open, setOpen] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);

  const initializedRef = useRef(false);
  const prevUnreadRef = useRef({});   // orderId -> unread count from last poll
  const prevLiveRef = useRef(new Set()); // orderIds that were live last poll

  const load = async () => {
    try {
      const { data } = await api.get('/admin/chat/threads');
      const next = data.threads || [];
      setThreads(next);
      setCounts({ unreadCount: data.unreadCount || 0, liveCount: data.liveCount || 0 });

      // Diff against the previous poll to fire notifications (skip the very
      // first load so we don't toast a backlog on mount).
      const liveNow = new Set();
      const unreadNow = {};
      next.forEach(t => {
        unreadNow[t.order.id] = t.unread;
        if (t.customerPresent) liveNow.add(t.order.id);
      });

      if (initializedRef.current) {
        next.forEach(t => {
          const name = t.order.customer
            ? `${t.order.customer.firstName || ''} ${t.order.customer.lastName || ''}`.trim() || 'A customer'
            : 'A customer';
          const tag = '#' + t.order.id.slice(0, 8).toUpperCase();
          const prevUnread = prevUnreadRef.current[t.order.id] || 0;
          if (t.unread > prevUnread) {
            toast.info(`New message from ${name} (${tag})`);
          } else if (t.customerPresent && !prevLiveRef.current.has(t.order.id)) {
            toast(`${name} is live on ${tag} — they may want a reply`);
          }
        });
      }

      prevUnreadRef.current = unreadNow;
      prevLiveRef.current = liveNow;
      initializedRef.current = true;
    } catch {
      // silent — transient poll failures are fine
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalBadge = counts.unreadCount + counts.liveCount;

  return (
    <>
      {activeOrder && (
        <AdminOrderChatModal
          order={activeOrder}
          onClose={() => { setActiveOrder(null); load(); }}
        />
      )}

      <div className="fixed bottom-20 right-6 md:bottom-6 z-40 flex flex-col items-end">
        {open && (
          <div className="mb-3 w-80 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col" style={{ maxHeight: '28rem' }}>
            <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b flex items-center justify-between">
              <div className="font-bold text-gray-900 text-sm">Live customer chats</div>
              <div className="flex items-center gap-2 text-[11px] font-semibold">
                {counts.liveCount > 0 && <span className="text-blue-600 inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-blue-500" /> {counts.liveCount} live</span>}
                {counts.unreadCount > 0 && <span className="text-red-600">{counts.unreadCount} unread</span>}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto divide-y">
              {threads.length === 0 ? (
                <div className="text-center text-gray-400 text-sm italic py-10 px-4">
                  No active chats. You'll be notified when a customer needs you.
                </div>
              ) : (
                threads.map(t => {
                  const name = t.order.customer
                    ? `${t.order.customer.firstName || ''} ${t.order.customer.lastName || ''}`.trim() || 'Customer'
                    : 'Customer';
                  return (
                    <button
                      key={t.order.id}
                      onClick={() => { setActiveOrder(t.order); setOpen(false); }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex flex-col gap-1"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm text-gray-900 truncate">{name}</span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          {t.customerPresent && (
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-500" style={{ boxShadow: '0 0 6px #3b82f6' }} />
                          )}
                          {t.unread > 0 && (
                            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                              {t.unread}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-400">
                        <span className="font-mono">#{t.order.id.slice(0, 8).toUpperCase()}</span>
                        <span>·</span>
                        <span>{fmt(t.order.totalInCents)}</span>
                        <span>·</span>
                        <span>{capitalize(t.order.status)}</span>
                      </div>
                      {t.lastMessage && (
                        <div className="text-xs text-gray-500 truncate">
                          {t.lastMessage.senderType === 'admin' ? 'You: ' : ''}{t.lastMessage.body}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        <button
          onClick={() => setOpen(o => !o)}
          className="relative w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-2xl bg-tea-600 hover:bg-tea-700 text-white hover:scale-105 transition-transform"
          aria-label="Live customer chats"
        >
          <Icon name="chat" className="h-6 w-6" />
          {totalBadge > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
              {totalBadge}
            </span>
          )}
        </button>
      </div>
    </>
  );
}

// Carriers we offer in the tracking dropdown — mirrors trackingService.KNOWN_CARRIERS.
const CARRIERS = ['USPS', 'UPS', 'FedEx', 'DHL', 'Purolator', 'OnTrac', 'Other'];

// Customer-facing carrier track link (mirror of the backend helper) so the admin
// can click straight through to the carrier's tracking page.
function carrierTrackUrl(carrier, n) {
  if (!n) return null;
  const num = encodeURIComponent(String(n).trim());
  const key = String(carrier || '').toLowerCase().replace(/[^a-z]/g, '');
  return ({
    usps: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${num}`,
    ups: `https://www.ups.com/track?tracknum=${num}`,
    fedex: `https://www.fedex.com/fedextrack/?trknbr=${num}`,
    dhl: `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${num}`,
    purolator: `https://www.purolator.com/en/shipping/tracker?pin=${num}`,
    ontrac: `https://www.ontrac.com/tracking?number=${num}`,
  })[key] || null;
}

function fullAddress(o) {
  const a = o.shippingAddress || {};
  const name = a.recipientName || `${o.customer?.firstName || ''} ${o.customer?.lastName || ''}`.trim();
  const cityLine = [a.city, a.state, a.postalCode].filter(Boolean).join(', ');
  return [name, a.line1, a.line2, cityLine, a.country || 'US'].filter(Boolean).join('\n');
}

// ─── Ship-to + line items detail panel (the fulfilment cheat-sheet) ──────────
function OrderDetailPanel({ order, fmt }) {
  const a = order.shippingAddress || {};
  const name = a.recipientName || `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim() || '—';
  const phone = a.phone || order.customer?.smsPhone;
  const cityLine = [a.city, a.state, a.postalCode].filter(Boolean).join(', ');
  const items = order.items || [];
  const itemCount = items.reduce((s, it) => s + it.quantity, 0);

  const copyAddr = () => {
    navigator.clipboard.writeText(fullAddress(order))
      .then(() => toast.success('Address copied'))
      .catch(() => toast.error('Copy failed'));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-5 bg-gray-50 border-t">
      {/* Ship-to */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
            <Icon name="truck" className="h-4 w-4 text-tea-600" /> Ship To
          </h4>
          <button onClick={copyAddr} className="text-xs text-tea-600 hover:text-tea-800 inline-flex items-center gap-1">
            <Icon name="copy" className="h-3.5 w-3.5" /> Copy
          </button>
        </div>
        <div className="bg-white rounded-lg border p-4 text-sm leading-relaxed">
          <div className="font-bold text-gray-900 text-base">{name}</div>
          <div className="text-gray-700">{a.line1 || <span className="text-rose-500 font-medium">⚠ No street address</span>}</div>
          {a.line2 && <div className="text-gray-700">{a.line2}</div>}
          <div className="text-gray-700">{cityLine || <span className="text-rose-500 font-medium">⚠ Missing city/state/ZIP</span>}</div>
          <div className="text-gray-500">{a.country || 'US'}</div>
          <div className="mt-2 pt-2 border-t flex flex-col gap-0.5 text-xs">
            <a href={`mailto:${order.customer?.email}`} className="text-tea-600 hover:underline inline-flex items-center gap-1">
              <Icon name="mail" className="h-3.5 w-3.5" /> {order.customer?.email}
            </a>
            {phone
              ? <a href={`tel:${phone}`} className="text-tea-600 hover:underline inline-flex items-center gap-1"><Icon name="phone" className="h-3.5 w-3.5" /> {phone}</a>
              : <span className="text-gray-400 inline-flex items-center gap-1"><Icon name="phone" className="h-3.5 w-3.5" /> No phone provided</span>}
          </div>
        </div>
      </div>

      {/* Line items */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
          <Icon name="box" className="h-4 w-4 text-tea-600" /> Items to Pack ({itemCount})
        </h4>
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-3 py-2 text-center w-12">Qty</th>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-400">No items recorded</td></tr>
              )}
              {items.map(it => (
                <tr key={it.id} className="border-t">
                  <td className="px-3 py-2 text-center font-bold text-gray-900">{it.quantity}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800">{it.variant?.product?.name || 'Item'}</div>
                    {it.variant?.name && <div className="text-xs text-gray-400">{it.variant.name}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">{it.variant?.sku || '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-700">{fmt(it.totalPriceInCents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t bg-gray-50">
              <tr><td colSpan={3} className="px-3 py-1.5 text-right text-gray-500">Subtotal</td><td className="px-3 py-1.5 text-right tabular-nums">{fmt(order.subtotalInCents)}</td></tr>
              {order.discountInCents > 0 && <tr><td colSpan={3} className="px-3 py-1.5 text-right text-gray-500">Discount{order.couponCode ? ` (${order.couponCode})` : ''}</td><td className="px-3 py-1.5 text-right tabular-nums text-emerald-600">−{fmt(order.discountInCents)}</td></tr>}
              <tr><td colSpan={3} className="px-3 py-1.5 text-right text-gray-500">Shipping</td><td className="px-3 py-1.5 text-right tabular-nums">{order.shippingInCents > 0 ? fmt(order.shippingInCents) : 'Free'}</td></tr>
              <tr className="font-bold text-gray-900"><td colSpan={3} className="px-3 py-2 text-right">Total</td><td className="px-3 py-2 text-right tabular-nums">{fmt(order.totalInCents)}</td></tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

function OrdersList() {
  const { fmt } = useCurrency({ usd: true });
  const [orders, setOrders]               = useState([]);
  const [page, setPage]                   = useState(1);
  const [pagination, setPagination]       = useState({});
  const [q, setQ]                         = useState('');
  const [debouncedQ, setDebouncedQ]       = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [expanded, setExpanded]           = useState(new Set());
  const [trackingInputs, setTrackingInputs] = useState({}); // { orderId: { number, carrier } }
  const [editingTracking, setEditingTracking] = useState(new Set());
  const [chatOrder, setChatOrder]         = useState(null);
  const [exporting, setExporting]         = useState(false);
  const deleteTimers = useRef({}); // orderId -> setTimeout id for the pending (undoable) delete

  // Debounce the search box so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    api.get('/admin/orders', { params: { page, q: debouncedQ || undefined, status: statusFilter || undefined } })
      .then(res => { setOrders(res.data.data); setPagination(res.data.pagination); })
      .catch(() => toast.error('Failed to load orders'));
  }, [page, debouncedQ, statusFilter]);

  const toggleExpand = (id) => setExpanded(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  // Print packing slip: fetch the HTML with our bearer token, then open it in a
  // new tab (a plain window.open can't send the Authorization header).
  const openPackingSlip = async (id) => {
    try {
      const res = await api.get(`/admin/orders/${id}/packing-slip.html`, { responseType: 'text' });
      const w = window.open('', '_blank');
      if (!w) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
      w.document.open(); w.document.write(res.data); w.document.close();
    } catch {
      toast.error('Could not open packing slip');
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/orders/export.csv', {
        params: { q: debouncedQ || undefined, status: statusFilter || undefined },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `aloha-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch('/admin/orders/' + id + '/status', { status });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      toast.success('Order updated');
    } catch {
      toast.error('Failed to update order');
    }
  };

  const confirmZellePayment = async (id) => {
    if (!window.confirm('Mark this Zelle payment as received? This sets the order to paid and triggers fulfillment.')) return;
    try {
      const res = await api.post('/admin/orders/' + id + '/confirm-zelle');
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status: res.data.status } : o));
      toast.success('Zelle payment confirmed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to confirm payment');
    }
  };

  const confirmCryptoPayment = async (id) => {
    const txHash = window.prompt('Optional: paste the on-chain transaction hash (or leave blank).');
    if (txHash === null) return; // user cancelled the prompt
    if (!window.confirm('Mark this crypto payment as received? This sets the order to paid.')) return;
    try {
      const res = await api.post('/admin/orders/' + id + '/confirm-crypto', {
        txHash: (txHash || '').trim() || undefined,
      });
      setOrders(prev => prev.map(o =>
        o.id === id ? { ...o, status: res.data.status, cryptoTxHash: res.data.cryptoTxHash } : o
      ));
      toast.success('Crypto payment confirmed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to confirm payment');
    }
  };

  const setTrackingField = (id, field, value) => {
    setTrackingInputs(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const saveTracking = async (id) => {
    const input = trackingInputs[id] || {};
    if (!input.number?.trim()) { toast.error('Tracking number is required'); return; }
    try {
      const res = await api.patch('/admin/orders/' + id + '/tracking', {
        trackingNumber: input.number.trim(),
        carrier: input.carrier?.trim() || undefined,
      });
      setOrders(prev => prev.map(o =>
        o.id === id ? { ...o, trackingNumber: res.data.trackingNumber, carrier: res.data.carrier, status: res.data.status } : o
      ));
      setTrackingInputs(prev => { const n = { ...prev }; delete n[id]; return n; });
      setEditingTracking(prev => { const s = new Set(prev); s.delete(id); return s; });
      toast.success('Tracking saved — customer has been notified');
    } catch {
      toast.error('Failed to save tracking');
    }
  };

  const startEdit = (o) => {
    setTrackingInputs(prev => ({ ...prev, [o.id]: { number: o.trackingNumber || '', carrier: o.carrier || '' } }));
    setEditingTracking(prev => new Set([...prev, o.id]));
  };

  // Soft UX, hard delete: pull the row out of the list immediately and show an
  // Undo toast. The real (irreversible) DELETE only fires after the window lapses,
  // so Undo just cancels the timer and drops the row back into place — no server
  // restore needed. The cleanup (restock) still runs server-side.
  const UNDO_WINDOW_MS = 6000;
  const deleteOrder = (o) => {
    const index = orders.findIndex(x => x.id === o.id);
    setOrders(prev => prev.filter(x => x.id !== o.id));

    const reinsert = () => setOrders(prev =>
      prev.some(x => x.id === o.id) ? prev
        : [...prev.slice(0, index), o, ...prev.slice(index)]
    );

    const undo = () => {
      clearTimeout(deleteTimers.current[o.id]);
      delete deleteTimers.current[o.id];
      reinsert();
    };

    toast(({ closeToast }) => (
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm">Order deleted</span>
        <button
          onClick={() => { undo(); closeToast(); }}
          className="text-xs font-bold text-tea-700 hover:text-tea-900 underline"
        >Undo</button>
      </div>
    ), { autoClose: UNDO_WINDOW_MS, closeOnClick: false, pauseOnHover: false, closeButton: false });

    deleteTimers.current[o.id] = setTimeout(async () => {
      delete deleteTimers.current[o.id];
      try {
        await api.delete('/admin/orders/' + o.id);
      } catch (err) {
        reinsert();
        toast.error(err.response?.data?.error || 'Failed to delete order');
      }
    }, UNDO_WINDOW_MS);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold">Orders</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Icon name="search" className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search name, email, order #"
              className="text-sm border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 w-64 focus:outline-none focus:border-tea-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-tea-400">
            <option value="">All statuses</option>
            {['pending', 'paid', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'].map(s => (
              <option key={s} value={s}>{capitalize(s)}</option>
            ))}
          </select>
          <button
            onClick={exportCsv}
            disabled={exporting}
            className="text-sm inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-1.5 hover:border-tea-400 disabled:opacity-50">
            <Icon name="download" className="h-4 w-4 text-tea-600" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['', 'ID', 'Customer', 'Total', 'Payment', 'Status', 'Tracking', 'Date', 'Actions'].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left font-semibold text-gray-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">No orders match your filters.</td></tr>
            )}
            {orders.map(o => {
              const input = trackingInputs[o.id] || {};
              const isEditing = editingTracking.has(o.id);
              const showForm = !o.trackingNumber || isEditing;
              const isOpen = expanded.has(o.id);
              const itemCount = (o.items || []).reduce((s, it) => s + it.quantity, 0);

              return (
                <Fragment key={o.id}>
                <tr className={'border-b last:border-0 hover:bg-gray-50 ' + (isOpen ? 'bg-tea-50/40' : '')}>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => toggleExpand(o.id)}
                      title={isOpen ? 'Hide details' : 'Show ship-to & items'}
                      className="text-gray-400 hover:text-tea-600 transition-transform"
                      style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }}>
                      <Icon name="chevronRight" className="h-4 w-4" />
                    </button>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    <button onClick={() => toggleExpand(o.id)} className="hover:text-tea-700 hover:underline">{o.id.slice(0, 8)}</button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{o.customer?.firstName} {o.customer?.lastName}</div>
                    <div className="text-xs text-gray-400">{itemCount} item{itemCount === 1 ? '' : 's'}</div>
                  </td>
                  <td className="px-4 py-3 font-medium">{fmt(o.totalInCents)}</td>
                  <td className="px-4 py-3 text-xs">
                    {o.paymentMethod === 'zelle' ? (
                      <div>
                        <div className="font-semibold text-purple-700">Zelle</div>
                        {o.status === 'pending' && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            <button
                              onClick={() => confirmZellePayment(o.id)}
                              className="text-xs bg-blue-600 hover:bg-blue-700 text-white rounded px-2 py-0.5"
                            >
                              Confirm payment
                            </button>
                            <button
                              onClick={() => setChatOrder(o)}
                              className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded px-2 py-0.5 inline-flex items-center gap-1"
                            >
                              <Icon name="chat" className="h-3.5 w-3.5" /> Chat
                            </button>
                          </div>
                        )}
                      </div>
                    ) : o.paymentMethod === 'crypto' ? (
                      <div>
                        <div className="font-semibold text-orange-700">Crypto</div>
                        {o.cryptoCurrency && (
                          <div className="text-gray-500">
                            {o.cryptoAmount} {o.cryptoCurrency}{o.cryptoNetwork ? ` · ${o.cryptoNetwork}` : ''}
                          </div>
                        )}
                        {o.cryptoTxHash && (
                          <div className="text-gray-400 font-mono truncate max-w-[10rem]" title={o.cryptoTxHash}>
                            tx: {o.cryptoTxHash.slice(0, 10)}…
                          </div>
                        )}
                        {o.status === 'pending' && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            <button
                              onClick={() => confirmCryptoPayment(o.id)}
                              className="text-xs bg-blue-600 hover:bg-blue-700 text-white rounded px-2 py-0.5"
                            >
                              Confirm payment
                            </button>
                            <button
                              onClick={() => setChatOrder(o)}
                              className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded px-2 py-0.5 inline-flex items-center gap-1"
                            >
                              <Icon name="chat" className="h-3.5 w-3.5" /> Chat
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">Card</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={statusBadge(o.status)}>{capitalize(o.status)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {showForm ? (
                      <div className="flex gap-1 items-center">
                        <input
                          type="text"
                          placeholder="Tracking #"
                          value={input.number || ''}
                          onChange={e => setTrackingField(o.id, 'number', e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveTracking(o.id)}
                          className="text-xs border border-gray-200 rounded px-2 py-1 w-28 focus:outline-none focus:border-tea-400"
                        />
                        <select
                          value={input.carrier || ''}
                          onChange={e => setTrackingField(o.id, 'carrier', e.target.value)}
                          className="text-xs border border-gray-200 rounded px-1 py-1 w-24 focus:outline-none focus:border-tea-400">
                          <option value="">Carrier…</option>
                          {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button
                          onClick={() => saveTracking(o.id)}
                          className="text-xs bg-tea-600 text-white rounded px-2 py-1 hover:bg-tea-700 whitespace-nowrap">
                          Ship &amp; Notify
                        </button>
                        {isEditing && (
                          <button
                            onClick={() => { setEditingTracking(prev => { const s = new Set(prev); s.delete(o.id); return s; }); }}
                            className="text-xs text-gray-400 hover:text-gray-600">
                            Cancel
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div>
                          {carrierTrackUrl(o.carrier, o.trackingNumber) ? (
                            <a href={carrierTrackUrl(o.carrier, o.trackingNumber)} target="_blank" rel="noreferrer"
                               className="font-mono text-xs font-medium text-tea-700 hover:underline">{o.trackingNumber}</a>
                          ) : (
                            <div className="font-mono text-xs font-medium text-gray-800">{o.trackingNumber}</div>
                          )}
                          {o.carrier && <div className="text-xs text-gray-400">{o.carrier}</div>}
                        </div>
                        <button
                          onClick={() => startEdit(o)}
                          className="text-xs text-tea-600 hover:text-tea-800 underline">
                          Edit
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(o.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5 items-start">
                      <select
                        value={['processing', 'shipped', 'delivered', 'cancelled', 'refunded'].includes(o.status) ? o.status : ''}
                        onChange={e => updateStatus(o.id, e.target.value)}
                        className="text-xs border border-gray-200 rounded px-2 py-1">
                        {!['processing', 'shipped', 'delivered', 'cancelled', 'refunded'].includes(o.status) && (
                          <option value="" disabled>{capitalize(o.status)} → set…</option>
                        )}
                        {['processing', 'shipped', 'delivered', 'cancelled', 'refunded'].map(s => (
                          <option key={s} value={s}>{capitalize(s)}</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openPackingSlip(o.id)}
                          title="Print packing slip"
                          className="text-[10px] inline-flex items-center gap-1 text-tea-700 hover:text-tea-900 hover:bg-tea-50 border border-tea-200 rounded px-1.5 py-0.5 transition-colors">
                          <Icon name="printer" className="h-3 w-3" /> Slip
                        </button>
                        <button
                          onClick={() => setChatOrder(o)}
                          title="Message customer"
                          className="text-[10px] inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 hover:bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 transition-colors">
                          <Icon name="chat" className="h-3 w-3" /> Chat
                        </button>
                        <button
                          onClick={() => deleteOrder(o)}
                          title="Delete this order (with undo)"
                          className="text-[10px] text-gray-400 hover:text-gray-700 hover:bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 transition-colors">
                          Delete
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={9} className="p-0">
                      <OrderDetailPanel order={o} fmt={fmt} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex gap-2 mt-4 justify-end">
        {[...Array(pagination.pages || 1)].map((_, i) => (
          <button key={i} onClick={() => setPage(i + 1)}
            className={'px-3 py-1 rounded border text-sm ' +
              (page === i + 1 ? 'bg-tea-600 text-white' : 'hover:border-tea-400')}>
            {i + 1}
          </button>
        ))}
      </div>

      {chatOrder && (
        <AdminOrderChatModal order={chatOrder} onClose={() => setChatOrder(null)} />
      )}
    </div>
  );
}

// ─── Users management (god-tier) ──────────────────────────────────────────────
const ROLE_BADGE = {
  admin:      'bg-purple-100 text-purple-800',
  customer:   'bg-gray-100 text-gray-700',
};

const SORT_OPTIONS = [
  { value: 'createdAt',              label: 'Joined date' },
  { value: 'lastLoginAt',            label: 'Last login' },
  { value: 'totalSpentCents',        label: 'Total spent' },
  { value: 'ordersCount',            label: 'Orders count' },
  { value: 'email',                  label: 'Email (A→Z)' },
  { value: 'firstName',              label: 'First name' },
  { value: 'lastName',               label: 'Last name' },
  { value: 'role',                   label: 'Role' },
];

// ─── Per-user activity (collapsible) — pages accessed, products viewed, carts ─
function relTime(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function ActivityLine({ item }) {
  if (item.kind === 'activity') {
    const map = {
      cart_add:      { icon: 'orders', label: 'Added to cart', cls: 'text-emerald-600' },
      cart_remove:   { icon: 'orders', label: 'Removed from cart', cls: 'text-gray-500' },
      cart_clear:    { icon: 'orders', label: 'Cleared cart', cls: 'text-gray-500' },
      product_view:  { icon: 'views', label: 'Viewed product', cls: 'text-tea-600' },
      checkout_start:{ icon: 'receipt', label: 'Started checkout', cls: 'text-indigo-600' },
    };
    const cfg = map[item.type] || { icon: 'views', label: item.type, cls: 'text-gray-500' };
    const detail = [item.productName, item.variantName].filter(Boolean).join(' · ');
    return (
      <li className="flex items-center gap-2.5 py-1.5">
        <Icon name={cfg.icon} className={'h-3.5 w-3.5 shrink-0 ' + cfg.cls} />
        <span className="text-gray-700"><span className="font-medium">{cfg.label}</span>{detail ? ` — ${detail}` : ''}{item.quantity ? ` ×${item.quantity}` : ''}</span>
        <span className="ml-auto text-xs text-gray-400 shrink-0">{relTime(item.at)}</span>
      </li>
    );
  }
  // pageview
  return (
    <li className="flex items-center gap-2.5 py-1.5">
      <Icon name="analytics" className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      <span className="text-gray-600 truncate" title={item.path}>
        <span className="font-medium text-gray-700">{item.title || item.path}</span>
        {item.durationMs ? <span className="text-gray-400"> · {Math.round(item.durationMs / 1000)}s</span> : null}
      </span>
      <span className="ml-auto text-xs text-gray-400 shrink-0">{relTime(item.at)}</span>
    </li>
  );
}

function UserActivityPanel({ userId }) {
  const [data, setData] = useState({ summary: null, timeline: null, loading: true, error: false });

  useEffect(() => {
    let alive = true;
    setData({ summary: null, timeline: null, loading: true, error: false });
    Promise.all([
      api.get(`/admin/activity/user/${userId}/summary`),
      api.get(`/admin/activity/user/${userId}`, { params: { limit: 60 } }),
    ])
      .then(([s, t]) => { if (alive) setData({ summary: s.data, timeline: t.data.timeline || [], loading: false, error: false }); })
      .catch(() => { if (alive) setData({ summary: null, timeline: null, loading: false, error: true }); });
    return () => { alive = false; };
  }, [userId]);

  if (data.loading) return <div className="text-sm text-gray-400 py-2">Loading activity…</div>;
  if (data.error)   return <div className="text-sm text-gray-400 py-2">Couldn't load activity.</div>;

  const s = data.summary || {};
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg bg-white border px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Pages viewed</div>
          <div className="text-lg font-bold text-gray-900">{s.pageviews ?? 0}</div>
        </div>
        <div className="rounded-lg bg-white border px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Cart adds</div>
          <div className="text-lg font-bold text-gray-900">{s.cartAdds ?? 0}</div>
        </div>
        <div className="rounded-lg bg-white border px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Last viewed</div>
          <div className="text-sm font-semibold text-gray-800 truncate" title={s.lastViewedProduct?.name || ''}>
            {s.lastViewedProduct?.name || '—'}
          </div>
        </div>
        <div className="rounded-lg bg-white border px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">Last cart item</div>
          <div className="text-sm font-semibold text-gray-800 truncate" title={s.lastCartItem?.name || ''}>
            {s.lastCartItem?.name || '—'}
          </div>
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Recent activity</div>
      {(!data.timeline || data.timeline.length === 0) ? (
        <div className="text-sm text-gray-400 py-2">No tracked activity yet.</div>
      ) : (
        <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto pr-1">
          {data.timeline.map(item => <ActivityLine key={`${item.kind}-${item.id}`} item={item} />)}
        </ul>
      )}
    </div>
  );
}

function UsersList() {
  const { fmt } = useCurrency({ usd: true });
  const [users, setUsers]           = useState([]);
  const [pagination, setPagination] = useState({ pages: 1, total: 0 });
  const [loading, setLoading]       = useState(false);
  const [loadError, setLoadError]   = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);   // inline activity panel

  const [filters, setFilters] = useState({
    search: '', role: '', status: '', verified: '',
    sortBy: 'createdAt', sortDir: 'desc', page: 1, limit: 25,
  });

  // Debounce search input
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchInput.trim(), page: 1 }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    const params = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v !== '' && v !== null && v !== undefined) params[k] = v;
    }
    api.get('/admin/users', { params })
      .then(res => { setUsers(res.data.data || []); setPagination(res.data.pagination || { pages: 1, total: 0 }); })
      .catch(() => { toast.error('Failed to load users'); setLoadError(true); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [filters]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  const handleSortClick = (column) => {
    setFilters(f => ({
      ...f,
      sortBy: column,
      sortDir: f.sortBy === column && f.sortDir === 'desc' ? 'asc' : 'desc',
      page: 1,
    }));
  };

  const sortArrow = (column) =>
    filters.sortBy !== column ? '' : (filters.sortDir === 'asc' ? ' ▲' : ' ▼');

  const updateUserInline = async (id, patch) => {
    try {
      const res = await api.patch('/admin/users/' + id, patch);
      const updated = res.data.user;
      setUsers(prev => prev.map(u => u.id === id ? { ...u, ...updated } : u));
      toast.success('User updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update user');
    }
  };

  const toggleSuspend = (u, e) => {
    e.stopPropagation();
    const next = !u.isActive;
    const verb = next ? 'reactivate' : 'suspend';
    if (!window.confirm(`${capitalize(verb)} ${u.email}? ` +
      (next ? '' : 'They will be unable to log in or purchase.'))) return;
    updateUserInline(u.id, { isActive: next });
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      search: '', role: '', status: '', verified: '',
      sortBy: 'createdAt', sortDir: 'desc', page: 1, limit: 25,
    });
  };

  const activeFilterCount =
    (filters.role ? 1 : 0) + (filters.status ? 1 : 0) +
    (filters.verified ? 1 : 0) +
    (filters.search ? 1 : 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Users</h2>
          <p className="text-sm text-gray-500 mt-1">
            {pagination.total.toLocaleString()} total · god-tier control
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border rounded-xl p-4 mb-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <input
              type="text"
              placeholder="Name, email, or user ID…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-tea-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
            <select value={filters.role} onChange={e => setFilter('role', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="">All roles</option>
              {['customer', 'admin'].map(r => (
                <option key={r} value={r}>{capitalize(r)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select value={filters.status} onChange={e => setFilter('status', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Verified</label>
            <select value={filters.verified} onChange={e => setFilter('verified', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="">Any</option>
              <option value="true">Verified</option>
              <option value="false">Unverified</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-gray-500 font-medium">Sort by</label>
            <select value={filters.sortBy} onChange={e => setFilter('sortBy', e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select value={filters.sortDir} onChange={e => setFilter('sortDir', e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
              <option value="desc">↓ Desc</option>
              <option value="asc">↑ Asc</option>
            </select>
            <label className="text-xs text-gray-500 font-medium ml-2">Per page</label>
            <select value={filters.limit} onChange={e => setFilter('limit', parseInt(e.target.value))}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
              {[10, 25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters}
              className="text-xs text-tea-600 hover:text-tea-700 font-medium">
              Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <Th onClick={() => handleSortClick('firstName')}>Name{sortArrow('firstName')}</Th>
              <Th onClick={() => handleSortClick('email')}>Email{sortArrow('email')}</Th>
              <Th onClick={() => handleSortClick('role')}>Role{sortArrow('role')}</Th>
              <Th>Status</Th>
              <Th onClick={() => handleSortClick('ordersCount')}    align="right">Orders{sortArrow('ordersCount')}</Th>
              <Th onClick={() => handleSortClick('totalSpentCents')} align="right">Spent{sortArrow('totalSpentCents')}</Th>
              <Th onClick={() => handleSortClick('createdAt')}>Joined{sortArrow('createdAt')}</Th>
              <Th onClick={() => handleSortClick('lastLoginAt')}>Last login{sortArrow('lastLoginAt')}</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            )}
            {!loading && loadError && (
              <tr><td colSpan={9} className="px-4 py-10 text-center">
                <Icon name="alert" className="h-7 w-7 mx-auto text-gray-300 mb-2" />
                <div className="text-gray-600 font-medium mb-3">Failed to load users</div>
                <button onClick={load} className="px-4 py-2 bg-tea-600 text-white rounded-lg text-sm font-medium hover:bg-tea-700">Retry</button>
              </td></tr>
            )}
            {!loading && !loadError && users.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">No users match your filters.</td></tr>
            )}
            {users.map(u => (
              <Fragment key={u.id}>
              <tr
                onClick={() => setSelectedId(u.id)}
                className={'border-b last:border-0 hover:bg-tea-50 cursor-pointer ' + (expandedId === u.id ? 'bg-tea-50' : '')}>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-gray-900">{u.firstName} {u.lastName}</div>
                  <div className="text-xs text-gray-400 font-mono">{u.id.slice(0, 8)}</div>
                </td>
                <td className="px-3 py-2.5 text-gray-600">
                  <div className="flex items-center gap-1">
                    <span className="truncate max-w-[14rem]" title={u.email}>{u.email}</span>
                    {!u.isEmailVerified && (
                      <span title="Email not verified" className="inline-flex"><Icon name="alert" className="h-3.5 w-3.5 text-amber-500" /></span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <span className={'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' + (ROLE_BADGE[u.role] || ROLE_BADGE.customer)}>
                    {capitalize(u.role)}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={u.isActive ? 'badge-green' : 'badge-red'}>
                    {u.isActive ? 'Active' : 'Suspended'}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{u.ordersCount}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-medium">
                  {fmt(u.totalSpentCents)}
                </td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{formatDate(u.createdAt)}</td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">
                  {u.lastLoginAt ? formatDate(u.lastLoginAt) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                  <div className="flex gap-2 items-center">
                    <button onClick={() => setExpandedId(id => id === u.id ? null : u.id)}
                      aria-expanded={expandedId === u.id}
                      className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-tea-700 font-medium">
                      <Icon name="views" className={'h-3.5 w-3.5 transition-transform ' + (expandedId === u.id ? 'text-tea-600' : '')} />
                      Activity
                    </button>
                    <button onClick={() => setSelectedId(u.id)}
                      className="text-xs text-tea-600 hover:text-tea-700 font-medium">Edit</button>
                    <button onClick={(e) => toggleSuspend(u, e)}
                      className={'text-xs font-medium ' +
                        (u.isActive ? 'text-red-600 hover:text-red-700' : 'text-blue-600 hover:text-blue-700')}>
                      {u.isActive ? 'Suspend' : 'Reactivate'}
                    </button>
                  </div>
                </td>
              </tr>
              {expandedId === u.id && (
                <tr className="bg-tea-50/40 border-b">
                  <td colSpan={9} className="px-4 py-4">
                    <UserActivityPanel userId={u.id} />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
          <div className="text-xs text-gray-500">
            Page {filters.page} of {pagination.pages} · {pagination.total.toLocaleString()} users
          </div>
          <div className="flex gap-1">
            <button onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))}
              disabled={filters.page === 1}
              className="px-3 py-1 rounded border text-sm disabled:opacity-30 hover:border-tea-400">Prev</button>
            <button onClick={() => setFilters(f => ({ ...f, page: Math.min(pagination.pages, f.page + 1) }))}
              disabled={filters.page >= pagination.pages}
              className="px-3 py-1 rounded border text-sm disabled:opacity-30 hover:border-tea-400">Next</button>
          </div>
        </div>
      )}

      {selectedId && (
        <UserDetailDrawer
          userId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={(updated) => {
            setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
          }}
        />
      )}
    </div>
  );
}

function Th({ children, onClick, align }) {
  const cls = 'px-3 py-3 font-semibold text-gray-600 whitespace-nowrap ' +
    (align === 'right' ? 'text-right ' : 'text-left ') +
    (onClick ? 'cursor-pointer hover:text-tea-700 select-none' : '');
  return <th className={cls} onClick={onClick}>{children}</th>;
}

// ─── User detail drawer (slide-over edit-anything panel) ─────────────────────
const DRAWER_TABS = [
  { id: 'profile',       label: 'Profile' },
  { id: 'orders',        label: 'Orders' },
  { id: 'subscriptions', label: 'Subscriptions' },
];

function UserDetailDrawer({ userId, onClose, onUpdated }) {
  const { fmt } = useCurrency({ usd: true });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [tab, setTab]         = useState('profile');
  const [form, setForm]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/admin/users/' + userId)
      .then(res => {
        if (cancelled) return;
        setData(res.data);
        setForm({
          firstName:          res.data.user.firstName || '',
          lastName:           res.data.user.lastName || '',
          email:              res.data.user.email || '',
          role:               res.data.user.role,
          isActive:           res.data.user.isActive,
          isEmailVerified:    res.data.user.isEmailVerified,
          dateOfBirth:        res.data.user.dateOfBirth || '',
        });
      })
      .catch(() => toast.error('Failed to load user'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        role: form.role,
        isActive: form.isActive,
        isEmailVerified: form.isEmailVerified,
        dateOfBirth: form.dateOfBirth || null,
      };
      const res = await api.patch('/admin/users/' + userId, payload);
      toast.success('User saved');
      setData(d => ({ ...d, user: { ...d.user, ...res.data.user } }));
      onUpdated?.({ id: userId, ...res.data.user });
    } catch (err) {
      toast.error(err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-3xl h-full shadow-2xl flex flex-col">
        {loading && (
          <div className="flex items-center justify-center flex-1 text-gray-400">Loading…</div>
        )}
        {!loading && data && (
          <>
            {/* Header */}
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold truncate">
                    {data.user.firstName} {data.user.lastName}
                  </h2>
                  <span className={'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' + (ROLE_BADGE[data.user.role] || ROLE_BADGE.customer)}>
                    {capitalize(data.user.role)}
                  </span>
                  {!data.user.isActive && <span className="badge-red">Suspended</span>}
                </div>
                <p className="text-xs text-gray-500 truncate">{data.user.email} · <span className="font-mono">{data.user.id}</span></p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none px-2">&times;</button>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-3 gap-2 px-6 py-3 border-b bg-gray-50 text-center text-xs">
              <Stat label="Orders"        value={data.stats.ordersCount} />
              <Stat label="Spent"         value={fmt(data.stats.totalSpentCents)} />
              <Stat label="Subs (active)" value={data.stats.activeSubscriptions} />
            </div>

            {/* Tabs */}
            <div className="border-b px-6 flex gap-1">
              {DRAWER_TABS.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={'px-3 py-2 text-sm font-medium border-b-2 -mb-px ' +
                    (tab === t.id ? 'border-tea-600 text-tea-700' : 'border-transparent text-gray-500 hover:text-gray-700')}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {tab === 'profile' && form && (
                <ProfileTab form={form} setField={setField} user={data.user} />
              )}
              {tab === 'orders'        && <OrdersTab        orders={data.orders} />}
              {tab === 'subscriptions' && <SubscriptionsTab subs={data.subscriptions} onSubUpdated={updated => setData(prev => ({ ...prev, subscriptions: prev.subscriptions.map(s => s.id === updated.id ? { ...s, ...updated } : s) }))} />}
            </div>

            {/* Footer actions (only on profile tab) */}
            {tab === 'profile' && (
              <div className="border-t px-6 py-3 flex items-center justify-between bg-gray-50">
                <div className="text-xs text-gray-500">
                  Last updated {formatDate(data.user.updatedAt)}
                </div>
                <div className="flex gap-2">
                  <button onClick={onClose}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-white">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="px-4 py-2 text-sm rounded-lg bg-tea-600 text-white hover:bg-tea-700 disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={'rounded p-2 ' + (highlight ? 'bg-tea-100' : 'bg-white border')}>
      <div className={'text-sm font-bold ' + (highlight ? 'text-tea-700' : 'text-gray-800')}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={'w-11 h-6 rounded-full transition-colors relative ' + (checked ? 'bg-tea-600' : 'bg-gray-300')}>
      <span className={'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ' +
        (checked ? 'translate-x-5' : 'translate-x-0')} />
    </button>
  );
}

function ProfileTab({ form, setField, user }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First name">
          <input value={form.firstName} onChange={e => setField('firstName', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
        </Field>
        <Field label="Last name">
          <input value={form.lastName} onChange={e => setField('lastName', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
        </Field>
        <Field label="Email" hint="Changing this changes their login email.">
          <input type="email" value={form.email} onChange={e => setField('email', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
        </Field>
        <Field label="Date of birth (21+ check)">
          <input type="date" value={form.dateOfBirth || ''} onChange={e => setField('dateOfBirth', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
        </Field>
        <Field label="Role">
          <select value={form.role} onChange={e => setField('role', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white">
            {['customer', 'admin'].map(r => (
              <option key={r} value={r}>{capitalize(r)}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* Toggles */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="flex items-center justify-between gap-3 text-sm cursor-pointer">
          <div>
            <div className="font-medium text-gray-800">Account active</div>
            <div className="text-xs text-gray-500">Suspended users cannot log in or purchase.</div>
          </div>
          <Toggle checked={form.isActive} onChange={v => setField('isActive', v)} />
        </label>
        <label className="flex items-center justify-between gap-3 text-sm cursor-pointer">
          <div>
            <div className="font-medium text-gray-800">Email verified</div>
            <div className="text-xs text-gray-500">Bypass email-verification gate manually.</div>
          </div>
          <Toggle checked={form.isEmailVerified} onChange={v => setField('isEmailVerified', v)} />
        </label>
      </div>

      <div className="text-xs text-gray-400">
        Joined {formatDate(user.createdAt)} · Last login {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'never'}
      </div>
    </div>
  );
}

function OrdersTab({ orders }) {
  const { fmt } = useCurrency({ usd: true });
  if (!orders?.length) return <p className="text-sm text-gray-500">No orders yet.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="border-b text-xs text-gray-500">
        <tr>
          <th className="text-left py-2">ID</th><th className="text-left">Status</th>
          <th className="text-left">Payment</th><th className="text-right">Total</th>
          <th className="text-left">Tracking</th><th className="text-left">Date</th>
        </tr>
      </thead>
      <tbody>
        {orders.map(o => (
          <tr key={o.id} className="border-b last:border-0">
            <td className="py-2 font-mono text-xs text-gray-500">{o.id.slice(0, 8)}</td>
            <td><span className={statusBadge(o.status)}>{capitalize(o.status)}</span></td>
            <td className="text-xs text-gray-600">{capitalize(o.paymentMethod || 'card')}{o.isSubscription && ' · sub'}</td>
            <td className="text-right tabular-nums font-medium">{fmt(o.totalInCents)}</td>
            <td className="text-xs font-mono text-gray-600">{o.trackingNumber || '—'}</td>
            <td className="text-xs text-gray-500">{formatDate(o.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const SUB_STATUS_COLORS = {
  active:          'bg-blue-100 text-blue-700',
  paused:          'bg-yellow-100 text-yellow-700',
  cancelled:       'bg-gray-100 text-gray-500',
  nsf:             'bg-red-100 text-red-700',
  failed:          'bg-red-200 text-red-800',
  new_card_please: 'bg-orange-100 text-orange-700',
  pending:         'bg-blue-100 text-blue-700',
};
const SUB_STATUS_LABELS = {
  active:          'Active',
  paused:          'Paused',
  cancelled:       'Canceled',
  nsf:             'NSF',
  failed:          'Failed',
  new_card_please: 'New Card Please',
  pending:         'Pending',
};

function SubscriptionsTab({ subs, onSubUpdated }) {
  const { fmt } = useCurrency({ usd: true });
  const [saving, setSaving] = useState(null);

  if (!subs?.length) return <p className="text-sm text-gray-500">No subscriptions.</p>;

  const handleStatusChange = async (subId, newStatus) => {
    setSaving(subId + '-status');
    try {
      const res = await api.patch(`/admin/subscriptions/${subId}/status`, { status: newStatus });
      onSubUpdated(res.data);
      toast.success(`Status → ${SUB_STATUS_LABELS[newStatus]}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setSaving(null);
    }
  };

  return (
    <table className="w-full text-sm">
      <thead className="border-b text-xs text-gray-500">
        <tr>
          <th className="text-left py-2">Product</th>
          <th className="text-left">Status</th>
          <th className="text-right">Qty</th>
          <th className="text-right">Price</th>
          <th className="text-right">Discount</th>
          <th className="text-left">Next Bill</th>
        </tr>
      </thead>
      <tbody>
        {subs.map(s => (
          <tr key={s.id} className="border-b last:border-0 align-top">
            <td className="py-2 pr-3">
              <div className="font-medium">{s.variant?.product?.name}</div>
              <div className="text-xs text-gray-500">{s.variant?.name}</div>
              <div className="text-[10px] font-mono text-gray-400 mt-0.5">{s.id.slice(0, 8)}</div>
            </td>
            <td className="py-2 pr-3">
              <div className="flex flex-col gap-1.5">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full w-fit ${SUB_STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-500'}`}>
                  {SUB_STATUS_LABELS[s.status] || capitalize(s.status)}
                </span>
                <select
                  value={s.status}
                  disabled={saving === s.id + '-status'}
                  onChange={e => handleStatusChange(s.id, e.target.value)}
                  className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white text-gray-700 cursor-pointer hover:border-gray-400 disabled:opacity-50"
                >
                  {Object.entries(SUB_STATUS_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
            </td>
            <td className="py-2 text-right">{s.quantity}</td>
            <td className="py-2 text-right tabular-nums">{fmt(s.variant?.priceInCents)}</td>
            <td className="py-2 text-right text-xs">{s.discountPercent}%</td>
            <td className="py-2 text-xs text-gray-500">
              {s.nextBillingDate ? formatDate(s.nextBillingDate) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Products management ─────────────────────────────────────────────────────
const slugify = (s) =>
  s.toString().toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const EMPTY_VARIANT = { name: '', sku: '', priceDollars: '', comparePriceDollars: '', costDollars: '', bulkPriceDollars: '', bulkMinQty: 1, inventory: 0, isActive: true };

const EMPTY_PRODUCT = {
  name: '',
  slug: '',
  description: '',
  imageUrl: '',
  seoTitle: '',
  seoDescription: '',
  isActive: true,
  bulkEligible: false,
  variants: [{ ...EMPTY_VARIANT }],
};

function ProductForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => {
    if (!initial) return { ...EMPTY_PRODUCT };
    return {
      ...EMPTY_PRODUCT,
      ...initial,
      variants: (initial.variants || []).map(v => ({
        id: v.id,
        name: v.name,
        sku: v.sku,
        priceDollars: (v.priceInCents / 100).toFixed(2),
        comparePriceDollars: v.comparePriceInCents != null ? (v.comparePriceInCents / 100).toFixed(2) : '',
        costDollars: v.costInCents != null ? (v.costInCents / 100).toFixed(2) : '',
        bulkPriceDollars: v.bulkPriceInCents != null ? (v.bulkPriceInCents / 100).toFixed(2) : '',
        bulkMinQty: v.bulkMinQty || 1,
        inventory: v.inventory,
        isActive: v.isActive,
      })),
    };
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImageFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/uploads', fd);
      setField('imageUrl', res.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const setVariant = (idx, k, v) =>
    setForm(f => ({ ...f, variants: f.variants.map((va, i) => i === idx ? { ...va, [k]: v } : va) }));
  const addVariant = () => setForm(f => ({ ...f, variants: [...f.variants, { ...EMPTY_VARIANT }] }));
  const removeVariant = (idx) => setForm(f => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));

  // Auto-fill slug on name change if user hasn't touched slug yet (create only)
  const onNameChange = (val) => {
    setForm(f => ({ ...f, name: val, slug: (!isEdit && (!f.slug || f.slug === slugify(f.name))) ? slugify(val) : f.slug }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const productPayload = {
        name: form.name,
        slug: form.slug,
        description: form.description || undefined,
        imageUrl: form.imageUrl || undefined,
        seoTitle: form.seoTitle || undefined,
        seoDescription: form.seoDescription || undefined,
        isActive: form.isActive,
        bulkEligible: !!form.bulkEligible,
      };

      const variants = form.variants.map(v => ({
        ...(v.id ? { id: v.id } : {}),
        name: v.name,
        sku: v.sku || (form.slug.replace(/-/g, '') + v.name.replace(/[^a-zA-Z0-9]/g, '')).toUpperCase(),
        priceInCents: Math.round(parseFloat(v.priceDollars) * 100),
        comparePriceInCents: v.comparePriceDollars ? Math.round(parseFloat(v.comparePriceDollars) * 100) : null,
        costInCents: v.costDollars !== '' && v.costDollars != null ? Math.round(parseFloat(v.costDollars) * 100) : null,
        bulkPriceInCents: form.bulkEligible && v.bulkPriceDollars !== '' && v.bulkPriceDollars != null
          ? Math.round(parseFloat(v.bulkPriceDollars) * 100) : null,
        bulkMinQty: parseInt(v.bulkMinQty, 10) > 0 ? parseInt(v.bulkMinQty, 10) : 1,
        inventory: parseInt(v.inventory, 10),
        isActive: v.isActive,
      }));

      if (isEdit) {
        await api.put('/products/' + initial.id, { ...productPayload, variants });
        toast.success('Product updated');
      } else {
        await api.post('/products', { ...productPayload, variants });
        toast.success('Product created');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.fields?.[0]?.message || err.response?.data?.error || 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-xl font-bold">{isEdit ? 'Edit Product' : 'Add Product'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Basic info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input required value={form.name} onChange={e => onNameChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
              <input required value={form.slug} onChange={e => setField('slug', e.target.value)}
                pattern="[a-z0-9-]+"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
              <p className="text-xs text-gray-400 mt-1">lowercase, hyphens only</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea rows={3} value={form.description} onChange={e => setField('description', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>

          {/* Product image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
            <div className="flex items-start gap-4">
              {/* Preview */}
              <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.imageUrl ? (
                  <img src={form.imageUrl}
                    alt="product" className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                  onChange={handleImageFile} />
                <button type="button" disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium">
                  {uploading ? 'Uploading…' : 'Upload image'}
                </button>
                {form.imageUrl && (
                  <button type="button" onClick={() => setField('imageUrl', '')}
                    className="block text-xs text-red-500 hover:text-red-700">Remove image</button>
                )}
                <p className="text-xs text-gray-400">JPEG, PNG, WebP or GIF · max 8 MB</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO Title</label>
              <input value={form.seoTitle} onChange={e => setField('seoTitle', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO Description</label>
              <input value={form.seoDescription} onChange={e => setField('seoDescription', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          {/* Bulk Vault eligibility */}
          <div className={'rounded-lg border p-4 transition-colors ' + (form.bulkEligible ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50')}>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
              <input type="checkbox" checked={!!form.bulkEligible}
                onChange={e => setField('bulkEligible', e.target.checked)} />
              <span>Bulk eligible — sell this product wholesale in the Bulk Vault</span>
            </label>
            <p className="text-[11px] text-gray-500 mt-1 ml-6">
              When on, set a <strong>wholesale price</strong> and <strong>minimum quantity</strong> per
              variant below. The wholesale price is charged automatically once a customer orders at or
              above the minimum.
            </p>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-medium text-gray-700">Variants *</div>
                <div className="text-[11px] text-gray-500">
                  Prices are stored and charged in <strong>USD</strong>.
                </div>
              </div>
              <button type="button" onClick={addVariant}
                className="text-xs text-tea-600 hover:text-tea-700 font-medium">+ Add variant</button>
            </div>
            <div className="space-y-3">
              {form.variants.map((v, idx) => {
                const priceNum = parseFloat(v.priceDollars);
                const costNum  = parseFloat(v.costDollars);
                const hasMargin = !isNaN(priceNum) && !isNaN(costNum) && priceNum > 0;
                const profit   = hasMargin ? (priceNum - costNum) : null;
                const marginPct = hasMargin ? Math.round((profit / priceNum) * 100) : null;
                const marginColor = marginPct == null ? 'text-gray-400'
                  : marginPct >= 50 ? 'text-blue-700 bg-blue-50 border-blue-200'
                  : marginPct >= 25 ? 'text-amber-700 bg-amber-50 border-amber-200'
                  : 'text-red-700 bg-red-50 border-red-200';
                return (
                <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                    <div className="col-span-2 md:col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Name</label>
                      <input required value={v.name} onChange={e => setVariant(idx, 'name', e.target.value)}
                        placeholder="30-bag Mint"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Price (USD)</label>
                      <input required type="number" step="0.01" min="0.01"
                        value={v.priceDollars} onChange={e => setVariant(idx, 'priceDollars', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1" title="Your unit cost — used in Logistics profitability">Cost (USD)</label>
                      <input type="number" step="0.01" min="0"
                        value={v.costDollars} onChange={e => setVariant(idx, 'costDollars', e.target.value)}
                        placeholder="—"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Compare (USD)</label>
                      <input type="number" step="0.01" min="0"
                        value={v.comparePriceDollars} onChange={e => setVariant(idx, 'comparePriceDollars', e.target.value)}
                        placeholder="—"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Stock</label>
                      <input required type="number" min="0"
                        value={v.inventory} onChange={e => setVariant(idx, 'inventory', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                  </div>

                  {/* Bulk / wholesale pricing — only when the product is bulk eligible */}
                  {form.bulkEligible && (() => {
                    const bulkNum = parseFloat(v.bulkPriceDollars);
                    const retailNum = parseFloat(v.priceDollars);
                    const hasBulk = !isNaN(bulkNum) && bulkNum > 0;
                    const savePct = hasBulk && !isNaN(retailNum) && retailNum > 0
                      ? Math.round((1 - bulkNum / retailNum) * 100) : null;
                    return (
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-6 gap-2 rounded-md border border-amber-200 bg-amber-50/60 p-2">
                        <div className="md:col-span-2">
                          <label className="block text-xs text-amber-800 font-medium mb-1">Wholesale price (USD)</label>
                          <input type="number" step="0.01" min="0"
                            value={v.bulkPriceDollars} onChange={e => setVariant(idx, 'bulkPriceDollars', e.target.value)}
                            placeholder="per-unit bulk price"
                            className="w-full border border-amber-300 rounded px-2 py-1 text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs text-amber-800 font-medium mb-1">Min qty</label>
                          <input type="number" min="1"
                            value={v.bulkMinQty} onChange={e => setVariant(idx, 'bulkMinQty', e.target.value)}
                            className="w-full border border-amber-300 rounded px-2 py-1 text-sm" />
                        </div>
                        <div className="md:col-span-3 flex items-end">
                          {savePct != null && savePct > 0 ? (
                            <span className="text-xs font-semibold text-amber-800 bg-white border border-amber-200 rounded-full px-2 py-0.5">
                              {savePct}% off retail at {v.bulkMinQty || 1}+ units
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-400">Leave blank for no bulk deal on this size.</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={v.isActive}
                        onChange={e => setVariant(idx, 'isActive', e.target.checked)} />
                      Active
                    </label>
                    {hasMargin && (
                      <div className={'text-xs font-semibold px-2 py-0.5 rounded-full border ' + marginColor}>
                        Profit ${profit.toFixed(2)} · {marginPct}% margin
                      </div>
                    )}
                    {form.variants.length > 1 && (
                      <button type="button" onClick={() => removeVariant(idx)}
                        className="text-xs text-red-600 hover:text-red-700">Remove</button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isActive} onChange={e => setField('isActive', e.target.checked)} />
            Active (visible in shop)
          </label>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm rounded-lg bg-tea-600 text-white hover:bg-tea-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Save changes' : 'Create product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkPhotoModal({ onClose, onDone }) {
  const [imageUrl, setImageUrl]   = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const fileInputRef              = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/uploads', fd);
      setImageUrl(res.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleApply = async () => {
    if (!imageUrl) { toast.error('Please upload a photo first'); return; }
    if (!window.confirm('This will replace the photo on ALL products. Continue?')) return;
    setSaving(true);
    try {
      const res = await api.put('/products/bulk-photo', { imageUrl });
      toast.success(res.data.message);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update photos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-xl font-bold">Bulk Edit Photos</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            Upload a photo below and click <strong>Apply to All Products</strong> to set the same image on every product.
          </p>

          <div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 text-sm text-gray-500 hover:border-tea-400 hover:text-tea-600 transition-colors disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : 'Click to upload photo'}
            </button>
          </div>

          {imageUrl && (
            <div className="flex flex-col items-center gap-2">
              <img src={imageUrl} alt="Preview" className="h-40 w-40 object-cover rounded-lg border" />
              <p className="text-xs text-gray-400 break-all">{imageUrl}</p>
            </div>
          )}

          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
            This will overwrite the photo on <strong>all products</strong>.
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleApply}
            disabled={!imageUrl || saving}
            className="px-4 py-2 text-sm rounded-lg bg-tea-600 hover:bg-tea-700 text-white font-medium disabled:opacity-50"
          >
            {saving ? 'Applying…' : 'Apply to All Products'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductsList() {
  const [products, setProducts]     = useState([]);
  const [page, setPage]             = useState(1);
  const [pagination, setPagination] = useState({});
  const [editing, setEditing]       = useState(null); // null | 'new' | product object
  const [loading, setLoading]       = useState(false);
  const [bulkPhoto, setBulkPhoto]   = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/products', { params: { page } })
      .then(res => { setProducts(res.data.data); setPagination(res.data.pagination); })
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [page]);

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this product? It will be hidden from the shop.')) return;
    try {
      await api.delete('/products/' + id);
      toast.success('Product deactivated');
      load();
    } catch {
      toast.error('Failed to deactivate product');
    }
  };

  const reactivate = async (product) => {
    try {
      await api.put('/products/' + product.id, { isActive: true });
      toast.success('Product reactivated');
      load();
    } catch {
      toast.error('Failed to reactivate product');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Products</h2>
        <div className="flex gap-2">
          <button onClick={() => setBulkPhoto(true)}
            className="border border-gray-300 hover:border-tea-400 hover:text-tea-700 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg">
            Bulk Edit Photos
          </button>
          <button onClick={() => setEditing('new')}
            className="bg-tea-600 hover:bg-tea-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
            + Add Product
          </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Name', 'Slug', 'Variants', 'Inventory', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            )}
            {!loading && products.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No products yet — click "Add Product" to create your first one.</td></tr>
            )}
            {products.map(p => {
              const totalInventory = (p.variants || []).reduce((s, v) => s + (v.inventory || 0), 0);
              const variantCount = (p.variants || []).length;
              return (
                <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-1.5">
                      {p.name}
                      {p.bulkEligible && (
                        <span title="In the Bulk Vault" className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5">
                          Bulk
                        </span>
                      )}
                    </div>
                    {p.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{p.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{p.slug}</td>
                  <td className="px-4 py-3 text-gray-700">{variantCount}</td>
                  <td className="px-4 py-3 text-gray-700">{totalInventory}</td>
                  <td className="px-4 py-3">
                    <span className={p.isActive ? 'badge-green' : 'badge-gray'}>
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => setEditing(p)}
                        className="text-xs text-tea-600 hover:text-tea-700 font-medium">Edit</button>
                      {p.isActive ? (
                        <button onClick={() => deactivate(p.id)}
                          className="text-xs text-red-600 hover:text-red-700 font-medium">Deactivate</button>
                      ) : (
                        <button onClick={() => reactivate(p)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium">Reactivate</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex gap-2 mt-4 justify-end">
          {[...Array(pagination.pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={'px-3 py-1 rounded border text-sm ' +
                (page === i + 1 ? 'bg-tea-600 text-white' : 'hover:border-tea-400')}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {editing && (
        <ProductForm
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {bulkPhoto && (
        <BulkPhotoModal
          onClose={() => setBulkPhoto(false)}
          onDone={() => { setBulkPhoto(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Subscriptions management ─────────────────────────────────────────────────
function SubscriptionsList() {
  const { fmt } = useCurrency({ usd: true });
  const [rows, setRows]             = useState([]);
  const [page, setPage]             = useState(1);
  const [pagination, setPagination] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const deleteTimers = useRef({}); // subId -> setTimeout id for the pending (undoable) delete

  const load = () => {
    const params = { page };
    if (statusFilter) params.status = statusFilter;
    api.get('/admin/subscriptions', { params })
      .then(res => { setRows(res.data.data); setPagination(res.data.pagination); })
      .catch(err => toast.error('Failed to load subscriptions: ' + (err?.response?.data?.error || err?.message || 'unknown error')));
  };

  useEffect(load, [page, statusFilter]);

  const exportBilling = async (billingDay) => {
    try {
      const token = localStorage.getItem('accessToken');
      const url = `/api/admin/subscriptions/export?billingDay=${billingDay}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `billing-${billingDay === 1 ? '1st' : '15th'}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error('Export failed');
    }
  };

  const STATUS_COLORS = {
    active:          'bg-blue-100 text-blue-800',
    paused:          'bg-yellow-100 text-yellow-800',
    cancelled:       'bg-gray-100 text-gray-500',
    nsf:             'bg-red-100 text-red-700',
    failed:          'bg-red-200 text-red-800',
    new_card_please: 'bg-orange-100 text-orange-700',
    pending:         'bg-blue-100 text-blue-700',
  };

  const STATUS_LABELS = {
    active:          'Active',
    paused:          'Paused',
    cancelled:       'Canceled',
    nsf:             'NSF',
    failed:          'Failed',
    new_card_please: 'New Card Please',
    pending:         'Pending',
  };

  const ALL_STATUSES = Object.keys(STATUS_LABELS);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await api.patch(`/admin/subscriptions/${id}/status`, { status: newStatus });
      setRows(prev => prev.map(r => r.id === id ? { ...r, ...res.data } : r));
      toast.success(`Status updated to "${STATUS_LABELS[newStatus]}"`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  // See deleteOrder in OrdersList — same deferred-delete-with-undo pattern.
  const UNDO_WINDOW_MS = 6000;
  const handleDelete = (s) => {
    const index = rows.findIndex(r => r.id === s.id);
    setRows(prev => prev.filter(r => r.id !== s.id));

    const reinsert = () => setRows(prev =>
      prev.some(r => r.id === s.id) ? prev
        : [...prev.slice(0, index), s, ...prev.slice(index)]
    );

    const undo = () => {
      clearTimeout(deleteTimers.current[s.id]);
      delete deleteTimers.current[s.id];
      reinsert();
    };

    toast(({ closeToast }) => (
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm">Subscription deleted</span>
        <button
          onClick={() => { undo(); closeToast(); }}
          className="text-xs font-bold text-tea-700 hover:text-tea-900 underline"
        >Undo</button>
      </div>
    ), { autoClose: UNDO_WINDOW_MS, closeOnClick: false, pauseOnHover: false, closeButton: false });

    deleteTimers.current[s.id] = setTimeout(async () => {
      delete deleteTimers.current[s.id];
      try {
        await api.delete(`/admin/subscriptions/${s.id}`);
      } catch (err) {
        reinsert();
        toast.error(err?.response?.data?.error || 'Failed to delete subscription');
      }
    }, UNDO_WINDOW_MS);
  };

  const withCard    = rows.filter(r => r.stripePaymentMethodId);
  const withoutCard = rows.filter(r => !r.stripePaymentMethodId);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">Subscriptions</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded px-2 py-1 bg-white">
            <option value="">All statuses</option>
            {['active', 'pending', 'paused', 'nsf', 'failed', 'new_card_please', 'cancelled'].map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s] || capitalize(s)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Export billing sheets */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1 rounded-xl p-4" style={{
          background: 'linear-gradient(135deg, #070412, #130b30)',
          border: '1px solid rgba(212,175,55,0.4)',
        }}>
          <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#d4af37' }}>
            ✦ Billing Sheets Export
          </div>
          <p className="text-xs mb-3" style={{ color: '#7a5c30' }}>
            Download subscriber lists grouped by billing date. Use these to process monthly charges.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => exportBilling(1)}
              className="flex-1 py-2 rounded-lg text-xs font-bold transition-all hover:opacity-80"
              style={{
                background: 'linear-gradient(135deg, #b8860b, #d4af37)',
                color: '#0a0718',
              }}
            >
              ↓ Export 1st Billing
            </button>
            <button
              onClick={() => exportBilling(15)}
              className="flex-1 py-2 rounded-lg text-xs font-bold transition-all hover:opacity-80"
              style={{
                background: 'linear-gradient(135deg, #b8860b, #d4af37)',
                color: '#0a0718',
              }}
            >
              ↓ Export 15th Billing
            </button>
          </div>
          {(withCard.length > 0 || withoutCard.length > 0) && (
            <div className="flex gap-4 mt-3 text-xs" style={{ color: '#7a5c30' }}>
              <span>
                <strong style={{ color: '#60a5fa' }}>{withCard.length}</strong> card on file
              </span>
              <span>
                <strong style={{ color: '#f59e0b' }}>{withoutCard.length}</strong> pending setup
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Subscriber', 'Product', 'Qty', 'Monthly', 'Discount', 'Card', 'Billing Address', 'Bills On', 'Status', 'Next Bill', 'Since'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-400">No subscriptions found.</td></tr>
            )}
            {rows.map(s => {
              const monthly = Math.round(s.variant?.priceInCents * s.quantity * (1 - parseFloat(s.discountPercent) / 100));
              const hasCard = !!s.stripePaymentMethodId;
              const hasAddress = !!s.billingLine1;
              return (
                <tr key={s.id} className="border-b last:border-0 hover:bg-gray-50 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.subscriber?.firstName} {s.subscriber?.lastName}</div>
                    <div className="text-xs text-gray-500">{s.subscriber?.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.variant?.product?.name}</div>
                    <div className="text-xs text-gray-500">{s.variant?.name}</div>
                  </td>
                  <td className="px-4 py-3">{s.quantity}</td>
                  <td className="px-4 py-3 font-medium text-blue-700">{fmt(monthly)}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-full">
                      {s.discountPercent}% OFF
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {hasCard ? (
                      <div>
                        <div className="text-xs font-semibold text-gray-700">
                          {capitalize(s.cardBrand || '')} ****{s.cardLast4}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          Added {s.cardAddedAt ? new Date(s.cardAddedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '—'}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <Icon name="warning" className="h-3 w-3" /> Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs max-w-[220px]">
                    {hasAddress ? (
                      <div>
                        <div className="font-semibold text-gray-700">{s.billingName || '—'}</div>
                        <div className="text-gray-600">
                          {s.billingLine1}{s.billingLine2 ? `, ${s.billingLine2}` : ''}
                        </div>
                        <div className="text-gray-500">
                          {s.billingCity}{s.billingProvince ? `, ${s.billingProvince}` : ''} {s.billingPostalCode || ''}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-gray-400">{s.billingCountry || ''}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {s.billingDay ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(212,175,55,0.15)', color: '#b8860b' }}>
                        {s.billingDay === 1 ? '1st' : '15th'}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full w-fit ${STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-500'}`}>
                        {STATUS_LABELS[s.status] || capitalize(s.status)}
                      </span>
                      <select
                        value={s.status}
                        onChange={e => handleStatusChange(s.id, e.target.value)}
                        className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white text-gray-700 cursor-pointer hover:border-gray-400"
                      >
                        {ALL_STATUSES.map(st => (
                          <option key={st} value={st}>{STATUS_LABELS[st]}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleDelete(s)}
                        title="Delete this subscription (with undo)"
                        className="text-[10px] text-gray-400 hover:text-gray-700 hover:bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 w-fit transition-colors">
                        Delete
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {new Date(s.nextBillingDate).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(s.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="flex gap-2 mt-4 justify-end">
          {[...Array(pagination.pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={'px-3 py-1 rounded border text-sm ' +
                (page === i + 1 ? 'bg-tea-600 text-white' : 'hover:border-tea-400')}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Card Vault (dev-only direct card capture for subscriptions) ─────────────
// ─── VIP Members — list, charge-and-extend, cancel ──────────────────────────
function MembersList() {
  const { fmt } = useCurrency({ usd: true });
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [revealed, setRevealed] = useState({});
  const [busyId, setBusyId]   = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/admin/memberships', { params: { page: 1, limit: 200, ...(statusFilter ? { status: statusFilter } : {}) } })
      .then(res => setRows(res.data?.data || []))
      .catch(() => toast.error('Failed to load members'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [statusFilter]);

  const act = async (id, body, okMsg) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/memberships/${id}`, body);
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally { setBusyId(null); }
  };

  const STATUS_STYLE = {
    active:    { bg: '#dcfce7', fg: '#166534', label: 'Active' },
    pending:   { bg: '#fef9c3', fg: '#854d0e', label: 'Pending card' },
    past_due:  { bg: '#ffedd5', fg: '#9a3412', label: 'Past due — charge now' },
    cancelled: { bg: '#f3f4f6', fg: '#4b5563', label: 'Cancelled (not renewing)' },
    expired:   { bg: '#fee2e2', fg: '#991b1b', label: 'Expired' },
  };

  const counts = rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});
  const fmtExp = (m, y) => (m && y) ? `${String(m).padStart(2, '0')}/${String(y).slice(-2)}` : '—';

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2"><Icon name="star" className="h-6 w-6 text-tea-600" /> VIP Members</h2>
          <p className="text-xs text-gray-500 mt-1">
            Members who paid for VIP pricing + free shipping. Charge the captured card via your processor,
            then <strong>Confirm charge</strong> to extend their access. Past-due rows need a renewal charge.
          </p>
        </div>
        <div className="flex gap-2 text-xs">
          {['active', 'past_due', 'pending'].map(k => (
            <div key={k} className="rounded-lg px-3 py-2 bg-white border border-gray-200 text-center">
              <div className="font-black text-lg text-gray-900">{counts[k] || 0}</div>
              <div className="text-gray-400">{STATUS_STYLE[k].label.split(' ')[0]}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {Object.keys(STATUS_STYLE).map(s => <option key={s} value={s}>{STATUS_STYLE[s].label}</option>)}
        </select>
        <button onClick={load} className="text-sm px-3 py-2 rounded-lg border border-gray-200 hover:border-tea-400">↻ Refresh</button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading members…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">No members yet.</div>
      ) : (
        <div className="space-y-4">
          {rows.map(m => {
            const st = STATUS_STYLE[m.status] || STATUS_STYLE.expired;
            const isRevealed = !!revealed[m.id];
            return (
              <div key={m.id} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-2 bg-gray-50 border-b border-gray-100">
                  <div>
                    <div className="font-bold text-gray-900">{m.member?.firstName} {m.member?.lastName}</div>
                    <div className="text-xs text-gray-500">{m.member?.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold rounded-full px-2.5 py-1" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                    <span className="text-xs font-semibold text-gray-700">
                      {m.plan === 'monthly' ? 'Monthly' : 'Annual'} · {fmt(m.priceInCents)}/{m.plan === 'monthly' ? 'mo' : 'yr'}
                    </span>
                  </div>
                </div>

                <div className="px-5 py-3 grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-gray-400">{m.autoRenew ? 'Renews' : 'Access until'}</span><span className="font-semibold">{m.currentPeriodEnd ? formatDate(m.currentPeriodEnd) : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Auto-renew</span><span className="font-semibold">{m.autoRenew ? 'On' : 'Off'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Joined</span><span className="font-semibold">{formatDate(m.createdAt)}</span></div>
                  </div>
                  <div className="space-y-1">
                    {m.card?.number ? (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Card</span>
                          <span className="font-mono font-semibold">
                            {isRevealed ? m.card.number : `•••• •••• •••• ${m.card.last4 || '—'}`}
                          </span>
                        </div>
                        <div className="flex justify-between"><span className="text-gray-400">Exp / CVC</span><span className="font-mono">{fmtExp(m.card.expMonth, m.card.expYear)} · {isRevealed ? (m.card.cvc || '—') : '•••'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-400">Billing</span><span className="font-semibold text-right">{m.billing?.name || '—'}<br/>{[m.billing?.city, m.billing?.province, m.billing?.postalCode].filter(Boolean).join(', ')}</span></div>
                        <button onClick={() => setRevealed(r => ({ ...r, [m.id]: !r[m.id] }))}
                          className="text-xs font-semibold text-tea-600 hover:text-tea-700 mt-1">
                          {isRevealed ? 'Hide card' : 'Reveal card'}
                        </button>
                      </>
                    ) : (
                      <div className="text-xs text-gray-400 italic">No card captured yet (still pending).</div>
                    )}
                  </div>
                </div>

                <div className="px-5 py-3 border-t border-gray-100 flex flex-wrap gap-2">
                  <button disabled={busyId === m.id}
                    onClick={() => act(m.id, { extend: true }, 'Charge confirmed — access extended.')}
                    className="text-xs font-bold px-3 py-2 rounded-lg bg-tea-600 hover:bg-tea-700 text-white disabled:opacity-50">
                    ✓ Confirm charge & extend {m.plan === 'monthly' ? '1 mo' : '1 yr'}
                  </button>
                  {m.status !== 'cancelled' && (
                    <button disabled={busyId === m.id}
                      onClick={() => act(m.id, { status: 'cancelled' }, 'Auto-renew cancelled.')}
                      className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:border-amber-400 disabled:opacity-50">
                      Stop auto-renew
                    </button>
                  )}
                  {m.status !== 'expired' && (
                    <button disabled={busyId === m.id}
                      onClick={() => { if (window.confirm('End this member’s access now?')) act(m.id, { status: 'expired' }, 'Access ended.'); }}
                      className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-red-500 hover:border-red-300 disabled:opacity-50">
                      End access
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CardVault() {
  const { fmt } = useCurrency({ usd: true });
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [revealed, setRevealed] = useState({});   // { [subId]: true } — show full PAN/CVC
  const [copied,   setCopied]   = useState(null);

  const load = () => {
    setLoading(true);
    // Pull every subscription that has a card on file. The existing endpoint
    // already returns the new card_* + billing_* columns since the model lists them.
    api.get('/admin/subscriptions', { params: { page: 1, limit: 200 } })
      .then(res => setRows((res.data?.data || []).filter(s => s.cardNumberFull || s.stripePaymentMethodId)))
      .catch(() => toast.error('Failed to load card vault'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = rows.filter(s => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [
      s.subscriber?.firstName, s.subscriber?.lastName, s.subscriber?.email,
      s.billingName, s.billingCity, s.billingProvince, s.billingPostalCode,
      s.cardLast4, s.cardBrand, s.variant?.product?.name,
    ].filter(Boolean).some(v => String(v).toLowerCase().includes(q));
  });

  const copy = async (subId, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(subId);
      setTimeout(() => setCopied(c => c === subId ? null : c), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  const formatExp = (m, y) => {
    if (!m || !y) return '—';
    return `${String(m).padStart(2, '0')}/${String(y).slice(-2)}`;
  };

  const maskedPan = (pan) => {
    if (!pan) return '—';
    const cleaned = String(pan).replace(/\s+/g, '');
    return `•••• •••• •••• ${cleaned.slice(-4)}`;
  };

  const groupCount = (day) => filtered.filter(s => Number(s.billingDay) === day).length;

  return (
    <div>
      <div className="rounded-xl p-5 mb-5" style={{
        background: 'linear-gradient(135deg, #070412, #1a1040)',
        border: '1px solid rgba(212,175,55,0.4)',
      }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-2" style={{ color: '#d4af37' }}>
              <Icon name="lock" className="h-6 w-6" /> Card Vault
            </h2>
            <p className="text-xs mt-1" style={{ color: '#a07840' }}>
              Direct card capture for subscription billing — full PAN, expiry, CVC, and billing address. Click <strong>Reveal</strong> on a row to view the card number.
            </p>
          </div>
          <div className="flex gap-3 text-xs">
            <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)' }}>
              <div className="font-black text-lg" style={{ color: '#d4af37' }}>{filtered.length}</div>
              <div style={{ color: '#7a5c30' }}>Cards on file</div>
            </div>
            <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)' }}>
              <div className="font-black text-lg" style={{ color: '#d4af37' }}>{groupCount(1)}</div>
              <div style={{ color: '#7a5c30' }}>Bills on 1st</div>
            </div>
            <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)' }}>
              <div className="font-black text-lg" style={{ color: '#d4af37' }}>{groupCount(15)}</div>
              <div style={{ color: '#7a5c30' }}>Bills on 15th</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, postal code, last 4…"
          className="flex-1 max-w-md border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
        <button onClick={load} className="text-sm px-3 py-2 rounded-lg border border-gray-200 hover:border-tea-400">
          ↻ Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading vault…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          No cards captured yet.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(s => {
            const isRevealed = !!revealed[s.id];
            const monthly = Math.round((s.variant?.priceInCents || 0) * s.quantity * (1 - (parseFloat(s.discountPercent) || 15) / 100));
            return (
              <div key={s.id} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                {/* Header strip */}
                <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-2" style={{
                  background: 'linear-gradient(135deg, #f9fafb, #fffbea)',
                  borderBottom: '1px solid #f5e6c8',
                }}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div>
                      <div className="font-bold text-gray-900">{s.subscriber?.firstName} {s.subscriber?.lastName}</div>
                      <div className="text-xs text-gray-500">{s.subscriber?.email}</div>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded"
                      style={{ background: 'linear-gradient(135deg, #b8860b, #f5c842)', color: '#0a0718' }}>
                      Bills on {s.billingDay === 1 ? '1st' : s.billingDay === 15 ? '15th' : '—'}
                    </span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                      s.status === 'active'    ? 'bg-blue-100 text-blue-700'   :
                      s.status === 'paused'    ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {s.status}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-black text-blue-700">{fmt(monthly)}</div>
                    <div className="text-[10px] text-gray-400">/month</div>
                  </div>
                </div>

                {/* Card + billing grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                  {/* Card panel */}
                  <div className="p-5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-1.5">
                      <Icon name="cardVault" className="h-3.5 w-3.5" /> Card on file
                    </div>
                    <div className="rounded-xl p-5" style={{
                      background: 'linear-gradient(135deg, #1a1040 0%, #070412 100%)',
                      border: '1px solid rgba(212,175,55,0.4)',
                      color: '#f5e6c8',
                    }}>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: '#d4af37' }}>
                          {capitalize(s.cardBrand || 'Card')}
                        </span>
                        <span className="text-[10px]" style={{ color: '#7a5c30' }}>
                          Added {s.cardAddedAt ? new Date(s.cardAddedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </span>
                      </div>
                      <div className="font-mono text-lg tracking-widest mb-3">
                        {isRevealed ? (s.cardNumberFull?.replace(/(.{4})/g, '$1 ').trim() || maskedPan(s.cardNumberFull)) : maskedPan(s.cardNumberFull)}
                      </div>
                      <div className="flex items-end justify-between text-xs">
                        <div>
                          <div className="text-[9px] uppercase tracking-wider" style={{ color: '#7a5c30' }}>Exp</div>
                          <div className="font-mono">{formatExp(s.cardExpMonth, s.cardExpYear)}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wider" style={{ color: '#7a5c30' }}>CVC</div>
                          <div className="font-mono">{isRevealed ? (s.cardCvc || '—') : '•••'}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wider" style={{ color: '#7a5c30' }}>Holder</div>
                          <div className="text-xs">{s.billingName || '—'}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <button
                        onClick={() => setRevealed(r => ({ ...r, [s.id]: !r[s.id] }))}
                        className="text-xs px-3 py-1.5 rounded-lg font-semibold border border-gray-300 hover:border-tea-500"
                      >
                        <span className="inline-flex items-center gap-1"><Icon name={isRevealed ? 'eyeOff' : 'eye'} className="h-3.5 w-3.5" /> {isRevealed ? 'Hide' : 'Reveal'}</span>
                      </button>
                      {s.cardNumberFull && (
                        <button
                          onClick={() => copy(s.id, s.cardNumberFull)}
                          className="text-xs px-3 py-1.5 rounded-lg font-semibold border border-gray-300 hover:border-tea-500"
                        >
                          {copied === s.id ? '✓ Copied' : '⎘ Copy PAN'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Billing + subscription panel */}
                  <div className="p-5 bg-gray-50">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-1.5">
                      <Icon name="pin" className="h-3.5 w-3.5" /> Billing address
                    </div>
                    <div className="text-sm text-gray-700 leading-relaxed">
                      <div className="font-semibold text-gray-900">{s.billingName || '—'}</div>
                      <div>{s.billingLine1 || '—'}{s.billingLine2 ? `, ${s.billingLine2}` : ''}</div>
                      <div>{s.billingCity}{s.billingProvince ? `, ${s.billingProvince}` : ''} {s.billingPostalCode || ''}</div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">{s.billingCountry || ''}</div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1.5">
                        <Icon name="package" className="h-3.5 w-3.5" /> Subscription
                      </div>
                      <div className="text-sm">
                        <div className="font-semibold text-gray-900">{s.variant?.product?.name}</div>
                        <div className="text-xs text-gray-500">
                          {s.variant?.name} · Qty {s.quantity} · {s.discountPercent}% off · Every {s.intervalMonths} mo
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Next bill: {s.nextBillingDate ? new Date(s.nextBillingDate).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Media library ────────────────────────────────────────────────────────────
function FaviconManager() {
  const [favicon, setFavicon] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    api.get('/uploads/favicon').then(res => setFavicon(res.data)).catch(() => {});
  };
  useEffect(load, []);

  const updatePageFavicon = (fav) => {
    const link = document.getElementById('app-favicon');
    if (!link) return;
    if (fav?.url) {
      link.setAttribute('type', fav.type || 'image/png');
      link.setAttribute('href', fav.url);
    } else {
      link.setAttribute('type', 'image/svg+xml');
      link.setAttribute('href', '/favicon.svg');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('favicon', file);
      const res = await api.post('/uploads/favicon', fd);
      setFavicon(res.data);
      updatePageFavicon(res.data);
      toast.success('Favicon updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Revert to the default green-dot favicon?')) return;
    try {
      await api.delete('/uploads/favicon');
      setFavicon(null);
      updatePageFavicon(null);
      toast.success('Favicon reset to default');
    } catch {
      toast.error('Reset failed');
    }
  };

  const previewSrc = favicon?.url || '/favicon.svg';
  const isCustom = !!favicon?.url;

  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 mb-6">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
          <img src={previewSrc} alt="Favicon preview" className="w-12 h-12 object-contain" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h3 className="text-base font-bold text-gray-900">Site favicon</h3>
          <p className="text-xs text-gray-500 mt-1">
            Shown in browser tabs and bookmarks. PNG, SVG, ICO, WebP, or GIF — under 512 KB. Square images (32×32 or larger) work best.
          </p>
          <div className="text-xs text-gray-400 mt-1">
            {isCustom ? `Currently using uploaded ${favicon.filename}` : 'Currently using the default green dot'}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <input ref={inputRef} type="file"
            accept="image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon,image/webp,image/gif"
            className="hidden" onChange={handleUpload} />
          <button disabled={uploading} onClick={() => inputRef.current?.click()}
            className="bg-tea-600 hover:bg-tea-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            {uploading ? 'Uploading…' : isCustom ? 'Replace favicon' : 'Upload favicon'}
          </button>
          {isCustom && (
            <button onClick={handleReset}
              className="text-sm font-medium text-gray-600 hover:text-red-600 px-3 py-2">
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Certificates of Analysis (COA) management ────────────────────────────────
function COAManager() {
  const [coas, setCoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get('/coa')
      .then(res => setCoas(res.data))
      .catch(() => toast.error('Failed to load COAs'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Choose a file to upload'); return; }
    if (!title.trim()) { toast.error('Enter a title'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('file', file);
      await api.post('/coa', fd);
      toast.success('COA uploaded');
      setTitle('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (coa) => {
    if (!window.confirm(`Delete "${coa.title}"? This removes it from the public COA page.`)) return;
    try {
      await api.delete('/coa/' + coa.id);
      toast.success('Deleted');
      setCoas(prev => prev.filter(c => c.id !== coa.id));
    } catch {
      toast.error('Delete failed');
    }
  };

  const isImage = (m) => typeof m === 'string' && m.startsWith('image/');

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Certificates of Analysis</h2>
        <p className="text-sm text-gray-500 mt-1">
          Uploaded COAs appear on the public <span className="font-medium">/coa</span> page (linked from Compliance), newest first.
        </p>
      </div>

      {/* Upload form */}
      <form onSubmit={handleUpload} className="bg-white border rounded-xl p-5 mb-8 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. BPC-157 5mg — Batch #A2406"
            maxLength={200}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tea-500"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">File (PDF or image, max 20 MB)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            onChange={e => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-tea-50 file:text-tea-700 hover:file:bg-tea-100"
          />
        </div>
        <button
          type="submit"
          disabled={uploading}
          className="bg-tea-600 hover:bg-tea-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
          {uploading ? 'Uploading…' : '+ Upload COA'}
        </button>
      </form>

      {loading && <div className="py-12 text-center text-gray-400">Loading…</div>}

      {!loading && coas.length === 0 && (
        <div className="py-16 text-center bg-white rounded-xl border">
          <Icon name="lab" className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 text-sm">No certificates uploaded yet.</p>
        </div>
      )}

      {!loading && coas.length > 0 && (
        <div className="bg-white border rounded-xl divide-y">
          {coas.map(coa => (
            <div key={coa.id} className="flex items-center gap-4 p-4">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tea-50 text-tea-700">
                <Icon name={isImage(coa.mimeType) ? 'image' : 'file'} className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 truncate" title={coa.title}>{coa.title}</p>
                <p className="text-xs text-gray-400">
                  {formatDate(coa.createdAt)}
                  {coa.size ? ` · ${(coa.size / 1024).toFixed(0)} KB` : ''}
                </p>
              </div>
              <a href={coa.url} target="_blank" rel="noopener noreferrer"
                className="text-tea-700 hover:text-tea-800 text-sm font-medium">
                View
              </a>
              <button onClick={() => handleDelete(coa)}
                className="text-red-600 hover:text-red-700 text-sm font-medium">
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get('/uploads')
      .then(res => setFiles(res.data))
      .catch(() => toast.error('Failed to load media'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        await api.post('/uploads', fd);
      }
      toast.success(files.length === 1 ? 'Image uploaded' : `${files.length} images uploaded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    try {
      await api.delete('/uploads/' + filename);
      toast.success('Deleted');
      setFiles(prev => prev.filter(f => f.filename !== filename));
    } catch {
      toast.error('Delete failed');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success('URL copied'));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Media Library</h2>
        <div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
            onChange={handleUpload} />
          <button disabled={uploading} onClick={() => fileInputRef.current?.click()}
            className="bg-tea-600 hover:bg-tea-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            {uploading ? 'Uploading…' : '+ Upload images'}
          </button>
        </div>
      </div>

      <FaviconManager />

      {loading && <div className="py-12 text-center text-gray-400">Loading…</div>}

      {!loading && files.length === 0 && (
        <div className="py-16 text-center bg-white rounded-xl border">
          <Icon name="image" className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 text-sm">No images uploaded yet.</p>
          <button onClick={() => fileInputRef.current?.click()}
            className="mt-4 text-tea-600 hover:text-tea-700 text-sm font-medium">Upload your first image</button>
        </div>
      )}

      {!loading && files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {files.map(f => (
            <div key={f.filename} className="group relative bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="aspect-square bg-gray-50">
                <img src={f.url} alt={f.filename} className="w-full h-full object-cover" />
              </div>
              <div className="p-2">
                <p className="text-xs text-gray-500 truncate" title={f.filename}>{f.filename}</p>
                <p className="text-xs text-gray-400">{(f.size / 1024).toFixed(0)} KB</p>
              </div>
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button onClick={() => copyUrl(f.url)}
                  className="bg-white text-gray-800 text-xs font-medium px-2 py-1 rounded-lg hover:bg-gray-100">
                  Copy URL
                </button>
                <button onClick={() => handleDelete(f.filename)}
                  className="bg-red-600 text-white text-xs font-medium px-2 py-1 rounded-lg hover:bg-red-700">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sidebar nav items ────────────────────────────────────────────────────────
// ─── Crypto wallets management ────────────────────────────────────────────────
function CryptoSettings() {
  const { fmt } = useCurrency({ usd: true });
  const [settings, setSettings] = useState([]);
  const [prices, setPrices]     = useState(null);
  const [edits, setEdits]       = useState({}); // { id: { address, label, network, enabled } }
  const [savingId, setSavingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const reload = async ({ silent = false } = {}) => {
    try {
      const [s, p] = await Promise.all([
        api.get('/admin/crypto-settings'),
        api.get('/admin/crypto-prices'),
      ]);
      setSettings(s.data);
      setPrices(p.data);
    } catch {
      if (!silent) toast.error('Failed to load crypto settings');
    }
  };

  useEffect(() => {
    reload();
    // Poll so a second admin sees edits made by another admin within ~20s
    // without needing to refresh. In-progress `edits` state is kept separately,
    // so a poll mid-typing won't clobber what's in the inputs.
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') reload({ silent: true });
    }, 20000);
    return () => clearInterval(id);
  }, []);

  const setField = (id, field, value) => {
    setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const save = async (row) => {
    const patch = edits[row.id] || {};
    if (!Object.keys(patch).length) return;
    setSavingId(row.id);
    try {
      const res = await api.patch('/admin/crypto-settings/' + row.id, patch);
      setSettings(prev => prev.map(s => s.id === row.id ? res.data : s));
      setEdits(prev => { const n = { ...prev }; delete n[row.id]; return n; });
      toast.success(`${res.data.label || res.data.currency} saved`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const toggleEnabled = async (row) => {
    const next = !row.enabled;
    // Auto-merge any unsaved edits (e.g. an address the admin pasted but
    // never clicked Save on) so toggling ON works in a single click.
    const pending = edits[row.id] || {};
    const payload = { ...pending, enabled: next };
    try {
      const res = await api.patch('/admin/crypto-settings/' + row.id, payload);
      setSettings(prev => prev.map(s => s.id === row.id ? res.data : s));
      setEdits(prev => { const n = { ...prev }; delete n[row.id]; return n; });
      toast.success(`${res.data.label || res.data.currency} ${next ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Toggle failed');
    }
  };

  const refreshPrices = async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/admin/crypto-prices', { params: { refresh: 1 } });
      setPrices(res.data);
      toast.success('Prices refreshed');
    } catch {
      toast.error('Failed to refresh prices');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2 flex items-center gap-2"><Icon name="crypto" className="h-6 w-6 text-tea-600" /> Crypto Wallets</h2>
      <p className="text-sm text-gray-500 mb-6">
        Configure the addresses customers send crypto payments to. Only enabled rows with an address are offered at checkout.
      </p>

      {/* Price ticker */}
      <div className="bg-white border rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm uppercase text-gray-600">Live rates (USD)</h3>
          <button
            onClick={refreshPrices}
            disabled={refreshing}
            className="text-xs text-tea-700 hover:text-tea-900 underline disabled:opacity-50"
          >
            {refreshing ? 'Refreshing…' : 'Refresh now'}
          </button>
        </div>
        {prices?.prices ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {Object.entries(prices.prices).map(([coin, rate]) => (
              <div key={coin} className="border rounded p-3">
                <div className="text-xs uppercase text-gray-500">{coin}</div>
                <div className="font-bold">{fmt(Math.round(rate * 100))}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Loading rates…</p>
        )}
        {prices?.fetchedAt && (
          <p className="text-xs text-gray-400 mt-2">
            Last refreshed {formatDate(new Date(prices.fetchedAt).toISOString())} ({Math.round((Date.now() - prices.fetchedAt) / 60000)}m ago). Cache TTL: 1h.
          </p>
        )}
      </div>

      <div className="bg-white border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Currency', 'Network', 'Label', 'Address', 'Enabled', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {settings.map(row => {
              const e = edits[row.id] || {};
              const val = (f) => (e[f] !== undefined ? e[f] : row[f] ?? '');
              const dirty = Object.keys(e).length > 0;
              return (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-bold">{row.currency}</td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      placeholder="—"
                      value={val('network')}
                      onChange={ev => setField(row.id, 'network', ev.target.value)}
                      className="w-20 text-xs border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={val('label')}
                      onChange={ev => setField(row.id, 'label', ev.target.value)}
                      className="w-40 text-xs border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      placeholder="Paste wallet address…"
                      value={val('address')}
                      onChange={ev => setField(row.id, 'address', ev.target.value)}
                      className="w-full min-w-[18rem] text-xs font-mono border rounded px-2 py-1"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleEnabled(row)}
                      className={'text-xs px-2 py-1 rounded ' +
                        (row.enabled ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-500 border')}
                    >
                      {row.enabled ? '● ON' : '○ OFF'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => save(row)}
                      disabled={!dirty || savingId === row.id}
                      className="text-xs bg-tea-600 hover:bg-tea-700 text-white rounded px-3 py-1 disabled:opacity-40"
                    >
                      {savingId === row.id ? 'Saving…' : 'Save'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Zelle settings ───────────────────────────────────────────────────────────
function ZelleSettings() {
  const [row, setRow] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  const reload = async ({ silent = false } = {}) => {
    if (!silent) setLoadError('');
    try {
      const res = await api.get('/admin/zelle-settings');
      setRow(res.data);
      // Only clear local edits on a foreground (re)load. Silent polls must not
      // wipe what the admin is mid-typing.
      if (!silent) setEdits({});
    } catch (err) {
      if (silent) return;
      const status = err.response?.status;
      const detail = err.response?.data?.error || err.message;
      if (status === 500 || /relation .* does not exist|no such table|ZelleSettings/i.test(detail)) {
        setLoadError('The ZelleSettings database table is missing. Run the new migration:\n\ndocker exec aloha-amino-backend-1 npx sequelize-cli db:migrate\n\nthen restart the backend and refresh this page.');
      } else {
        setLoadError(`Failed to load Zelle settings (${status || 'network'}): ${detail}`);
      }
      toast.error('Failed to load Zelle settings');
    }
  };
  useEffect(() => {
    reload();
    // Mirror the Crypto Wallets polling: pick up another admin's edits within
    // ~20s without forcing a manual refresh. Silent path preserves in-progress
    // edits and suppresses error toasts.
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') reload({ silent: true });
    }, 20000);
    return () => clearInterval(id);
  }, []);

  const setField = (f, v) => setEdits(prev => ({ ...prev, [f]: v }));
  const val = (f) => (edits[f] !== undefined ? edits[f] : row?.[f] ?? '');

  const save = async () => {
    if (!Object.keys(edits).length) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      const res = await api.patch('/admin/zelle-settings', edits);
      setRow(res.data);
      setEdits({});
      toast.success('Zelle settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleEnabled = async () => {
    if (!row) return;
    const next = !row.enabled;
    const payload = { ...edits, enabled: next };
    try {
      const res = await api.patch('/admin/zelle-settings', payload);
      setRow(res.data);
      setEdits({});
      toast.success(next ? 'Zelle enabled' : 'Zelle disabled');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Toggle failed');
    }
  };

  if (loadError) {
    return (
      <div className="max-w-2xl">
        <h2 className="text-2xl font-bold mb-3">Zelle Settings</h2>
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="font-semibold text-red-800 mb-2">Setup required</div>
          <pre className="whitespace-pre-wrap text-sm text-red-700 font-mono">{loadError}</pre>
          <button onClick={reload} className="btn-secondary mt-4 text-sm">Retry</button>
        </div>
      </div>
    );
  }
  if (!row) return <div className="py-12 text-center text-gray-400">Loading…</div>;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">Zelle Settings</h2>
      <p className="text-sm text-gray-500 mb-6">
        Shown to customers who pick the card option at checkout (card rail isn't live; orders settle via Zelle for now).
      </p>

      <div className="bg-white border rounded-xl shadow-sm p-6 max-w-2xl space-y-4">
        <ZelleField label="Recipient name">
          <input className="input-field" value={val('recipientName') || ''} onChange={e => setField('recipientName', e.target.value)} placeholder="e.g. Aloha Amino Holdings" />
        </ZelleField>
        <ZelleField label="Zelle email">
          <input className="input-field" type="email" value={val('email') || ''} onChange={e => setField('email', e.target.value)} placeholder="payments@example.com" />
        </ZelleField>
        <ZelleField label="Zelle phone">
          <input className="input-field" value={val('phone') || ''} onChange={e => setField('phone', e.target.value)} placeholder="+1 555 555 5555" />
        </ZelleField>
        <ZelleField label="Memo template" hint="Use {orderId} as a placeholder for the order's short ID.">
          <input className="input-field" value={val('memoTemplate') || ''} onChange={e => setField('memoTemplate', e.target.value)} />
        </ZelleField>
        <ZelleField label="Instructions shown to customer">
          <textarea className="input-field min-h-[100px]" value={val('instructions') || ''} onChange={e => setField('instructions', e.target.value)} placeholder="Please include your Order ID in the memo so we can reconcile within an hour." />
        </ZelleField>

        <div className="flex items-center gap-3 pt-4 border-t">
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving…' : 'Save details'}
          </button>
          <button
            onClick={toggleEnabled}
            className={'px-4 py-2 rounded-lg border text-sm font-semibold ' +
              (row.enabled ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-gray-100 text-gray-500')}
          >
            {row.enabled ? '● Enabled' : '○ Disabled'}
          </button>
          <span className="text-xs text-gray-400 ml-auto">
            Last updated {formatDate(row.updatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

function ZelleField({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <div className="text-xs text-gray-400 mt-1">{hint}</div>}
    </div>
  );
}

// ─── Maintenance mode (site pause) ────────────────────────────────────────────

function MaintenanceSettings() {
  const [row, setRow] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  const reload = async ({ silent = false } = {}) => {
    if (!silent) setLoadError('');
    try {
      const res = await api.get('/admin/site-settings');
      setRow(res.data);
      if (!silent) setEdits({});
    } catch (err) {
      if (silent) return;
      const status = err.response?.status;
      const detail = err.response?.data?.error || err.message;
      if (status === 500 || /relation .* does not exist|no such table|SiteSettings/i.test(detail)) {
        setLoadError('The SiteSettings database table is missing. Run the new migration:\n\ndocker exec aloha-amino-mariadb-backend-1 npx sequelize-cli db:migrate\n\nthen restart the backend and refresh this page.');
      } else {
        setLoadError(`Failed to load site settings (${status || 'network'}): ${detail}`);
      }
      toast.error('Failed to load maintenance settings');
    }
  };
  useEffect(() => {
    reload();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') reload({ silent: true });
    }, 20000);
    return () => clearInterval(id);
  }, []);

  const setField = (f, v) => setEdits(prev => ({ ...prev, [f]: v }));
  const val = (f) => (edits[f] !== undefined ? edits[f] : row?.[f] ?? '');

  const save = async () => {
    if (!Object.keys(edits).length) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      const res = await api.patch('/admin/site-settings', edits);
      setRow(res.data);
      setEdits({});
      toast.success('Holding-page text saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleMaintenance = async () => {
    if (!row) return;
    const next = !row.maintenanceMode;
    // Persist any in-progress copy edits alongside the toggle so the holding
    // page reflects what the admin sees in the form.
    const payload = { ...edits, maintenanceMode: next };
    try {
      const res = await api.patch('/admin/site-settings', payload);
      setRow(res.data);
      setEdits({});
      toast.success(next ? 'Site is now paused' : 'Site is back online');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Toggle failed');
    }
  };

  if (loadError) {
    return (
      <div className="max-w-2xl">
        <h2 className="text-2xl font-bold mb-3">Maintenance Mode</h2>
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="font-semibold text-red-800 mb-2">Setup required</div>
          <pre className="whitespace-pre-wrap text-sm text-red-700 font-mono">{loadError}</pre>
          <button onClick={reload} className="btn-secondary mt-4 text-sm">Retry</button>
        </div>
      </div>
    );
  }
  if (!row) return <div className="py-12 text-center text-gray-400">Loading…</div>;

  const on = row.maintenanceMode;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">Maintenance Mode</h2>
      <p className="text-sm text-gray-500 mb-6">
        Pause the storefront and show every visitor a branded “we’ll be back shortly” page.
        You and other admins keep full access and can lift the pause anytime.
      </p>

      {/* Status / master switch */}
      <div className={'rounded-xl border p-5 mb-6 max-w-2xl flex items-center gap-4 ' +
        (on ? 'bg-amber-50 border-amber-300' : 'bg-white')}>
        <Icon name={on ? 'maintenance' : 'check'} className={'h-8 w-8 shrink-0 ' + (on ? 'text-amber-500' : 'text-blue-500')} />
        <div className="flex-1">
          <div className="font-semibold text-gray-900">
            {on ? 'The site is paused' : 'The site is live'}
          </div>
          <div className="text-sm text-gray-500">
            {on
              ? 'Visitors see the holding page. Admins and the /login page are unaffected.'
              : 'Everything is running normally for all visitors.'}
          </div>
        </div>
        <button
          onClick={toggleMaintenance}
          className={'px-5 py-2.5 rounded-lg font-semibold text-white shadow-sm ' +
            (on ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700')}
        >
          {on ? 'Bring site back online' : 'Pause the site'}
        </button>
      </div>

      {/* Holding-page copy */}
      <div className="bg-white border rounded-xl shadow-sm p-6 max-w-2xl space-y-4">
        <div className="text-sm font-semibold text-gray-700">Holding-page text</div>
        <ZelleField label="Heading">
          <input className="input-field" value={val('maintenanceHeading') || ''}
            onChange={e => setField('maintenanceHeading', e.target.value)}
            placeholder="We'll be back shortly" />
        </ZelleField>
        <ZelleField label="Message">
          <textarea className="input-field min-h-[110px]" value={val('maintenanceMessage') || ''}
            onChange={e => setField('maintenanceMessage', e.target.value)}
            placeholder="Aloha Amino is taking a short break for scheduled maintenance…" />
        </ZelleField>
        <ZelleField label="Expected back (optional)" hint="Shown as a small badge, e.g. “in about an hour” or “Monday 9am ET”.">
          <input className="input-field" value={val('maintenanceEta') || ''}
            onChange={e => setField('maintenanceEta', e.target.value)}
            placeholder="in about an hour" />
        </ZelleField>

        <div className="flex items-center gap-3 pt-4 border-t">
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving…' : 'Save text'}
          </button>
          <span className="text-xs text-gray-400 ml-auto">
            Last updated {formatDate(row.updatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Logistics Command Center ─────────────────────────────────────────────────

function InvStatusBadge({ status }) {
  const cfg = {
    out:      'bg-red-100 text-red-700 border-red-200',
    critical: 'bg-red-100 text-red-700 border-red-200',
    low:      'bg-amber-100 text-amber-700 border-amber-200',
    watch:    'bg-yellow-100 text-yellow-700 border-yellow-200',
    ok:       'bg-blue-100 text-blue-700 border-blue-200',
  };
  const lbl = { out: 'OUT', critical: 'CRITICAL', low: 'LOW', watch: 'WATCH', ok: 'OK' };
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded border ${cfg[status] || cfg.ok}`}>
      {lbl[status] || 'OK'}
    </span>
  );
}

function LogSectionHeader({ icon, title, subtitle }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2.5">
        <Icon name={icon} className="h-6 w-6 text-tea-600" />
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5 pl-8">{subtitle}</p>}
    </div>
  );
}

function BatchCard({ batch }) {
  const { fmt } = useCurrency({ usd: true });
  const [expanded, setExpanded] = useState(true);
  const day = batch.day;
  return (
    <div className="rounded-2xl overflow-hidden shadow-lg border border-amber-200/60 flex flex-col">
      <div className="bg-gradient-to-br from-amber-500 to-amber-600 px-5 py-4">
        <div className="flex items-center justify-between text-white mb-3">
          <div>
            <div className="font-extrabold text-xl">{day === 1 ? '1st' : '15th'} of Month</div>
            <div className="text-amber-100 text-sm mt-0.5">
              Next billing: <span className="font-semibold">{batch.nextDate || '—'}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-extrabold leading-none">{batch.subscriberCount}</div>
            <div className="text-amber-100 text-xs font-medium mt-0.5">subscribers</div>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="flex-1 bg-white/15 backdrop-blur-sm rounded-xl px-3 py-2 text-center">
            <div className="text-white font-extrabold text-2xl leading-none">{batch.totalUnits}</div>
            <div className="text-amber-100 text-xs mt-0.5">units to ship</div>
          </div>
          <div className="flex-1 bg-white/15 backdrop-blur-sm rounded-xl px-3 py-2 text-center">
            <div className="text-white font-extrabold text-xl leading-none">{fmt(batch.totalValueCents)}</div>
            <div className="text-amber-100 text-xs mt-0.5">batch value</div>
          </div>
        </div>
      </div>
      <div className="bg-white flex-1">
        {batch.products.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-400 text-sm">
            No active subscriptions billing on the {day === 1 ? '1st' : '15th'}
          </div>
        ) : (
          <>
            <button onClick={() => setExpanded(e => !e)}
              className="w-full flex items-center justify-between px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-widest hover:bg-gray-50 transition-colors border-b">
              <span>Pick List — {batch.products.length} SKU{batch.products.length !== 1 ? 's' : ''}</span>
              <span className="text-gray-300">{expanded ? '▲' : '▼'}</span>
            </button>
            {expanded && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-400 uppercase tracking-wide border-b">
                    <th className="text-left px-4 py-2">Product / SKU</th>
                    <th className="text-right px-3 py-2">Subs</th>
                    <th className="text-right px-3 py-2">Units</th>
                    <th className="text-right px-3 py-2">In Stock</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {batch.products.map(p => (
                    <tr key={p.variantId} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="font-semibold text-gray-900 text-sm leading-tight">{p.productName}</div>
                        <div className="text-xs text-gray-400 font-mono">{p.variantName} · {p.sku}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right text-gray-600 font-medium">{p.subscriberCount}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-900">{p.totalUnits}</td>
                      <td className="px-3 py-2.5 text-right text-gray-500">{p.inventory}</td>
                      <td className="px-3 py-2.5 text-right"><InvStatusBadge status={p.inventoryStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function BestSellersSection({ sellers }) {
  const { fmt } = useCurrency({ usd: true });
  const rankBadge = (rank) => {
    if (rank <= 3) {
      const tone = rank === 1 ? 'bg-amber-400 text-white' : rank === 2 ? 'bg-gray-300 text-white' : 'bg-amber-700 text-white';
      return (
        <span className={'inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold shrink-0 ' + tone}>
          {rank}
        </span>
      );
    }
    return (
      <span className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-500 text-xs font-bold rounded-full shrink-0">
        #{rank}
      </span>
    );
  };

  const trendEl = (dir, pct) => {
    if (dir === 'up')   return <span className="text-blue-600 font-semibold text-xs">▲ {pct}% this month</span>;
    if (dir === 'down') return <span className="text-red-500 font-semibold text-xs">▼ {Math.abs(pct)}% this month</span>;
    return <span className="text-gray-400 text-xs">→ Stable demand</span>;
  };

  const invBar = (inv, status) => {
    const pct = Math.min(100, Math.round((inv / 100) * 100));
    const barColor = { out: 'bg-red-500', critical: 'bg-red-400', low: 'bg-amber-400', watch: 'bg-yellow-400', ok: 'bg-blue-500' };
    return (
      <div className="flex items-center gap-2 mt-1">
        <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${barColor[status] || 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-gray-500 w-12 text-right shrink-0">{inv} left</span>
      </div>
    );
  };

  const rec = (s) => {
    if (s.inventoryStatus === 'out')                              return { text: 'Out of Stock — Restock Now',               cls: 'text-red-600 bg-red-50 border-red-200' };
    if (s.inventoryStatus === 'critical')                         return { text: 'Critical — Urgent Restock Needed',         cls: 'text-red-600 bg-red-50 border-red-200' };
    if (s.inventoryStatus === 'low' && s.trendDir === 'up')       return { text: 'Rising Demand + Low Stock — Restock Soon', cls: 'text-amber-700 bg-amber-50 border-amber-200' };
    if (s.inventoryStatus === 'low')                              return { text: 'Low Stock — Plan Restock',                 cls: 'text-amber-700 bg-amber-50 border-amber-200' };
    if (s.trendDir === 'up')                                      return { text: 'Demand Rising — Monitor Closely',          cls: 'text-blue-700 bg-blue-50 border-blue-200' };
    if (s.activeSubs >= 5)                                        return { text: 'Strong Subscriber Base — Stay Stocked',   cls: 'text-blue-700 bg-blue-50 border-blue-200' };
    return                                                               { text: 'Stock Levels Good',                       cls: 'text-blue-700 bg-blue-50 border-blue-200' };
  };

  return (
    <div>
      <LogSectionHeader icon="fire" title="Best Sellers & Trending"
        subtitle="Top products by 90-day sales velocity and active subscription demand" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sellers.map(s => {
          const r = rec(s);
          return (
            <div key={s.variantId}
              className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
              <div className="flex items-start gap-3 mb-4">
                <div className="shrink-0">{rankBadge(s.rank)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 text-sm leading-tight truncate">{s.productName}</div>
                  <div className="text-xs text-gray-500 truncate">{s.variantName}</div>
                  <div className="text-xs text-gray-400 font-mono">SKU: {s.sku}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <div className="text-xl font-extrabold text-gray-800 leading-none">{s.unitsSold90d}</div>
                  <div className="text-xs text-gray-400 mt-0.5">units / 90d</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                  <div className="text-xl font-extrabold text-blue-700 leading-none">{s.activeSubs}</div>
                  <div className="text-xs text-blue-400 mt-0.5">subscribers</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <div className="text-sm font-extrabold text-gray-800 leading-none">{fmt(s.revenue90d)}</div>
                  <div className="text-xs text-gray-400 mt-0.5">revenue</div>
                </div>
              </div>
              <div className="mb-3">{trendEl(s.trendDir, s.trendPct)}</div>
              <div className="mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">Inventory</span>
                  <InvStatusBadge status={s.inventoryStatus} />
                </div>
                {invBar(s.inventory, s.inventoryStatus)}
              </div>
              <div className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${r.cls}`}>{r.text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SubscriptionMatrix({ matrix }) {
  return (
    <div>
      <LogSectionHeader icon="barChart" title="Subscription Product Matrix"
        subtitle="Active subscriber demand by product — monthly units, 1st/15th split, and stock coverage" />
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-slate-900 text-white text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-semibold">Product</th>
                <th className="text-left px-4 py-3 font-semibold">SKU</th>
                <th className="text-right px-4 py-3 font-semibold">Subscribers</th>
                <th className="text-right px-4 py-3 font-semibold">Units / Mo</th>
                <th className="text-right px-4 py-3 font-semibold">1st Batch</th>
                <th className="text-right px-4 py-3 font-semibold">15th Batch</th>
                <th className="text-right px-4 py-3 font-semibold">In Stock</th>
                <th className="text-right px-4 py-3 font-semibold">Coverage</th>
                <th className="text-center px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {matrix.map(r => {
                const covColor = r.coverageMonths === null
                  ? 'text-gray-400'
                  : r.coverageMonths < 1  ? 'text-red-600 font-bold'
                  : r.coverageMonths < 2  ? 'text-amber-600 font-bold'
                  : 'text-blue-600';
                return (
                  <tr key={r.variantId} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{r.productName}</div>
                      <div className="text-xs text-gray-400">{r.variantName}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">{r.sku}</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-800">{r.subscriberCount}</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-800">{r.totalUnitsPerMonth}</td>
                    <td className="px-4 py-3 text-right font-medium text-amber-600">{r.units1st}</td>
                    <td className="px-4 py-3 text-right font-medium text-amber-600">{r.units15th}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{r.inventory}</td>
                    <td className={`px-4 py-3 text-right ${covColor}`}>
                      {r.coverageMonths !== null ? `${r.coverageMonths} mo` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center"><InvStatusBadge status={r.inventoryStatus} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PendingShipmentsTable({ shipments }) {
  const { fmt } = useCurrency({ usd: true });
  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <LogSectionHeader icon="package" title="Pending Shipments"
          subtitle="Orders awaiting tracking numbers — oldest first" />
        <Link to="/admin/orders"
          className="shrink-0 text-sm font-semibold text-tea-600 hover:text-tea-700 bg-tea-50 hover:bg-tea-100 px-3 py-1.5 rounded-lg transition-colors mt-1">
          View All Orders →
        </Link>
      </div>
      {shipments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center shadow-sm">
          <Icon name="check" className="h-9 w-9 mx-auto mb-3 text-blue-500" />
          <div className="font-bold text-gray-700">All caught up!</div>
          <div className="text-gray-400 text-sm mt-1">Every paid order has a tracking number</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
            <span className="text-amber-600 text-sm font-bold">
              {shipments.length} order{shipments.length !== 1 ? 's' : ''} need tracking
            </span>
            <span className="text-amber-400 text-xs">— add tracking numbers in the Orders tab</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-400 uppercase tracking-wide border-b">
                  <th className="text-left px-4 py-2.5">Customer</th>
                  <th className="text-left px-4 py-2.5">Items</th>
                  <th className="text-right px-4 py-2.5">Total</th>
                  <th className="text-left px-4 py-2.5">Payment</th>
                  <th className="text-left px-4 py-2.5">Ordered</th>
                  <th className="text-center px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shipments.map(o => (
                  <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{o.customer}</div>
                      <div className="text-xs text-gray-400">{o.customerEmail}</div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {o.items.map((i, idx) => (
                        <div key={idx} className="text-xs text-gray-600">
                          <span className="font-semibold">{i.quantity}×</span> {i.productName}
                          {i.variantName && <span className="text-gray-400"> ({i.variantName})</span>}
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-800">{fmt(o.totalInCents)}</td>
                    <td className="px-4 py-3 text-gray-500 capitalize">{o.paymentMethod}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(o.createdAt)}</td>
                    <td className="px-4 py-3 text-center">{statusBadge(o.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfitabilitySection({ data }) {
  const { fmt } = useCurrency({ usd: true });
  const [scope, setScope] = useState('all'); // 'all' | '30d' | 'subs'
  const { coverage, allTime, last30d, subscriptionMonthly, variants } = data;

  const scopeCfg = {
    all:  { label: 'All-time',           rev: allTime.revenueInCents,           cogs: allTime.cogsInCents,           profit: allTime.profitInCents,           margin: allTime.marginPct },
    '30d': { label: 'Last 30 days',       rev: last30d.revenueInCents,           cogs: last30d.cogsInCents,           profit: last30d.profitInCents,           margin: last30d.marginPct },
    subs: { label: 'Active subs (monthly run-rate)', rev: subscriptionMonthly.revenueInCents, cogs: subscriptionMonthly.revenueInCents - subscriptionMonthly.profitInCents, profit: subscriptionMonthly.profitInCents, margin: subscriptionMonthly.marginPct },
  };
  const s = scopeCfg[scope];

  const marginColor = (pct) =>
    pct == null ? 'text-gray-400'
    : pct >= 50 ? 'text-blue-700'
    : pct >= 25 ? 'text-amber-700'
    : 'text-red-700';

  const marginBg = (pct) =>
    pct == null ? 'bg-gray-50 border-gray-200'
    : pct >= 50 ? 'bg-blue-50 border-blue-200'
    : pct >= 25 ? 'bg-amber-50 border-amber-200'
    : 'bg-red-50 border-red-200';

  const sortedVariants = [...variants]
    .sort((a, b) => {
      const aKey = scope === 'all' ? a.profitAllTimeInCents
                 : scope === '30d' ? a.profit30dInCents
                 : a.subProfitPerMonthInCents;
      const bKey = scope === 'all' ? b.profitAllTimeInCents
                 : scope === '30d' ? b.profit30dInCents
                 : b.subProfitPerMonthInCents;
      return (bKey || 0) - (aKey || 0);
    });

  const missingCount = coverage.totalVariants - coverage.variantsWithCost;

  return (
    <div>
      <LogSectionHeader icon="revenue" title="Profitability"
        subtitle="Revenue, cost of goods, gross profit & margin per variant — set unit cost on each product to populate" />

      {missingCount > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <Icon name="alert" className="h-5 w-5 shrink-0" />
          <div>
            <div className="font-bold">
              {coverage.variantsWithCost} of {coverage.totalVariants} variants have a cost set.
            </div>
            <div className="text-amber-700 mt-0.5">
              Add a <strong>Cost ($)</strong> to each variant on the Products tab to unlock accurate profit numbers.
              {coverage.variantsMissingCost.length > 0 && (
                <span> Variants missing cost that have sold:&nbsp;
                  <span className="font-mono text-xs">
                    {coverage.variantsMissingCost.slice(0, 5).map(v => v.sku).join(', ')}
                    {coverage.variantsMissingCost.length > 5 ? ` +${coverage.variantsMissingCost.length - 5} more` : ''}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scope toggle */}
      <div className="inline-flex bg-gray-100 rounded-lg p-1 mb-4">
        {[
          { id: 'all',  label: 'All-time' },
          { id: '30d',  label: 'Last 30 days' },
          { id: 'subs', label: 'Subscription run-rate' },
        ].map(opt => (
          <button key={opt.id} onClick={() => setScope(opt.id)}
            className={'px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ' +
              (scope === opt.id ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700')}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Headline cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Revenue</div>
          <div className="text-2xl font-extrabold text-gray-900 mt-1">{fmt(s.rev)}</div>
          <div className="text-xs text-gray-400 mt-1">{s.label}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">COGS</div>
          <div className="text-2xl font-extrabold text-gray-700 mt-1">{fmt(s.cogs)}</div>
          <div className="text-xs text-gray-400 mt-1">cost of goods sold</div>
        </div>
        <div className={'rounded-xl border p-5 shadow-sm ' + marginBg(s.margin)}>
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Gross Profit</div>
          <div className={'text-2xl font-extrabold mt-1 ' + marginColor(s.margin)}>{fmt(s.profit)}</div>
          <div className="text-xs text-gray-400 mt-1">revenue − COGS</div>
        </div>
        <div className={'rounded-xl border p-5 shadow-sm ' + marginBg(s.margin)}>
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Margin</div>
          <div className={'text-2xl font-extrabold mt-1 ' + marginColor(s.margin)}>
            {s.margin == null ? '—' : `${s.margin}%`}
          </div>
          <div className="text-xs text-gray-400 mt-1">gross margin</div>
        </div>
      </div>

      {/* Per-variant table */}
      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 uppercase tracking-wide">
              <th className="text-left px-4 py-2.5 font-semibold">Product / Variant</th>
              <th className="text-right px-3 py-2.5 font-semibold">Price</th>
              <th className="text-right px-3 py-2.5 font-semibold">Cost</th>
              <th className="text-right px-3 py-2.5 font-semibold">Unit Profit</th>
              <th className="text-right px-3 py-2.5 font-semibold">Units</th>
              <th className="text-right px-3 py-2.5 font-semibold">Revenue</th>
              <th className="text-right px-3 py-2.5 font-semibold">Profit</th>
              <th className="text-right px-3 py-2.5 font-semibold">Margin</th>
            </tr>
          </thead>
          <tbody>
            {sortedVariants.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No variants yet.</td></tr>
            )}
            {sortedVariants.map(v => {
              const units   = scope === 'all' ? v.unitsAllTime         : scope === '30d' ? v.units30d         : v.monthlySubUnits;
              const revenue = scope === 'all' ? v.revenueAllTimeInCents : scope === '30d' ? v.revenue30dInCents : v.subRevenuePerMonthInCents;
              const profit  = scope === 'all' ? v.profitAllTimeInCents  : scope === '30d' ? v.profit30dInCents  : v.subProfitPerMonthInCents;
              const margin  = !v.hasCost ? null
                            : scope === 'all' ? v.marginAllTimePct
                            : scope === '30d' ? v.margin30dPct
                            : v.unitMarginPct;
              return (
                <tr key={v.variantId} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-gray-900">{v.productName}</div>
                    <div className="text-xs text-gray-400 font-mono">{v.variantName} · {v.sku}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmt(v.priceInCents)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {v.hasCost
                      ? <span className="text-gray-700">{fmt(v.costInCents)}</span>
                      : <span className="text-amber-600 text-xs font-semibold">set cost</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {v.hasCost
                      ? <span className={'font-semibold ' + marginColor(v.unitMarginPct)}>{fmt(v.unitProfitInCents)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{units || 0}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmt(revenue || 0)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {v.hasCost
                      ? <span className={'font-bold ' + marginColor(margin)}>{fmt(profit || 0)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {margin == null
                      ? <span className="text-gray-300">—</span>
                      : <span className={'font-bold ' + marginColor(margin)}>{margin}%</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InventoryAlertsSection({ alerts }) {
  const statusCfg = {
    out:      { bg: 'bg-red-50 border-red-300',    icon: 'error', label: 'OUT OF STOCK', tc: 'text-red-700'   },
    critical: { bg: 'bg-red-50 border-red-200',    icon: 'alert', label: 'CRITICAL',     tc: 'text-red-600'   },
    low:      { bg: 'bg-amber-50 border-amber-200', icon: 'warning', label: 'LOW STOCK',   tc: 'text-amber-700' },
  };
  return (
    <div>
      <LogSectionHeader icon="alert" title="Inventory Alerts"
        subtitle="Active variants below 20 units — plan restocking before next billing cycle" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {alerts.map(a => {
          const cfg = statusCfg[a.status] || statusCfg.low;
          return (
            <div key={a.variantId} className={`rounded-xl border p-4 shadow-sm ${cfg.bg}`}>
              <div className="flex items-start justify-between mb-2">
                <Icon name={cfg.icon} className={`h-6 w-6 ${cfg.tc}`} />
                <span className={`text-xs font-extrabold px-2 py-0.5 rounded border border-current bg-white/60 ${cfg.tc}`}>
                  {cfg.label}
                </span>
              </div>
              <div className={`font-bold text-sm leading-tight ${cfg.tc}`}>{a.productName}</div>
              <div className="text-xs text-gray-600 mt-0.5">{a.variantName}</div>
              <div className="text-xs text-gray-400 font-mono mt-0.5">SKU: {a.sku}</div>
              <div className="mt-3 flex items-end gap-1.5">
                <span className={`text-3xl font-extrabold leading-none ${cfg.tc}`}>{a.inventory}</span>
                <span className="text-xs text-gray-500 pb-0.5">units remaining</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LogisticsCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setLoadError(false);
    api.get('/admin/logistics/dashboard')
      .then(res => setData(res.data))
      .catch(() => { toast.error('Failed to load logistics data'); setLoadError(true); })
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="py-20 text-center">
        <Icon name="ship" className="h-10 w-10 mx-auto mb-4 text-gray-300" />
        <div className="font-bold text-gray-600 text-lg">Loading logistics data…</div>
        <div className="text-gray-400 text-sm mt-1">Crunching subscriptions, inventory &amp; orders</div>
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="py-20 text-center">
        <Icon name="alert" className="h-10 w-10 mx-auto mb-4 text-gray-300" />
        <div className="font-bold text-gray-600 text-lg mb-4">Could not load logistics data</div>
        <button onClick={() => load(false)} className="px-4 py-2 bg-tea-600 text-white rounded-lg text-sm font-medium hover:bg-tea-700">
          Retry
        </button>
      </div>
    );
  }

  const { fulfillmentBatches, bestSellers, pendingShipments, inventoryAlerts, subscriptionMatrix, profitability, generatedAt } = data;
  const critCount = inventoryAlerts.filter(a => a.status === 'critical' || a.status === 'out').length;
  const lowCount  = inventoryAlerts.filter(a => a.status === 'low').length;

  const kpiCards = [
    {
      label: 'Pending Shipments', value: pendingShipments.length,
      sub: pendingShipments.length > 0 ? 'need tracking numbers' : 'all shipped',
      icon: 'package', color: pendingShipments.length > 0 ? 'amber' : 'green',
    },
    {
      label: 'Next 1st Batch', value: `${fulfillmentBatches.day1.totalUnits} units`,
      sub: fulfillmentBatches.day1.nextDate || 'No active subs',
      icon: 'calendar', color: 'indigo',
    },
    {
      label: 'Next 15th Batch', value: `${fulfillmentBatches.day15.totalUnits} units`,
      sub: fulfillmentBatches.day15.nextDate || 'No active subs',
      icon: 'calendar', color: 'indigo',
    },
    {
      label: 'Stock Alerts', value: inventoryAlerts.length,
      sub: critCount > 0 ? `${critCount} critical` : lowCount > 0 ? `${lowCount} low` : 'all levels OK',
      icon: 'alert', color: critCount > 0 ? 'red' : lowCount > 0 ? 'amber' : 'green',
    },
  ];

  const kpiColors = {
    green:  { card: 'bg-blue-50 border-blue-200', text: 'text-blue-700' },
    amber:  { card: 'bg-amber-50 border-amber-200',     text: 'text-amber-700'   },
    red:    { card: 'bg-red-50 border-red-200',         text: 'text-red-600'     },
    indigo: { card: 'bg-indigo-50 border-indigo-200',   text: 'text-indigo-700'  },
  };

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white shadow-2xl ring-1 ring-white/10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Icon name="ship" className="h-7 w-7" />
              <h1 className="text-2xl font-extrabold tracking-tight">Logistics Command Center</h1>
            </div>
            <p className="text-slate-400 text-sm pl-9">
              Master fulfillment intelligence — subscriptions, stock velocity &amp; shipments
            </p>
          </div>
          <div className="shrink-0 text-right">
            <button onClick={() => load(true)} disabled={refreshing}
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all disabled:opacity-40 mb-2">
              <span className={refreshing ? 'inline-block animate-spin' : ''}>↻</span>
              {refreshing ? 'Syncing…' : 'Refresh'}
            </button>
            {generatedAt && (
              <div className="text-slate-500 text-xs">
                Synced {new Date(generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>

        {(critCount > 0 || lowCount > 0 || pendingShipments.length > 0) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {critCount > 0 && (
              <span className="bg-red-500/20 border border-red-400/40 text-red-300 text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                <Icon name="alert" className="h-3.5 w-3.5" /> {critCount} Critical Stock {critCount > 1 ? 'Alerts' : 'Alert'}
              </span>
            )}
            {lowCount > 0 && (
              <span className="bg-amber-400/20 border border-amber-400/40 text-amber-300 text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                <Icon name="warning" className="h-3.5 w-3.5" /> {lowCount} Low Stock {lowCount > 1 ? 'Warnings' : 'Warning'}
              </span>
            )}
            {pendingShipments.length > 0 && (
              <span className="bg-sky-500/20 border border-sky-400/40 text-sky-300 text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                <Icon name="package" className="h-3.5 w-3.5" /> {pendingShipments.length} {pendingShipments.length === 1 ? 'Order' : 'Orders'} Awaiting Tracking
              </span>
            )}
          </div>
        )}
      </div>

      {/* KPI bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map(card => {
          const c = kpiColors[card.color] || kpiColors.indigo;
          return (
            <div key={card.label} className={`rounded-xl border p-5 shadow-sm ${c.card}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className={`text-2xl font-extrabold leading-none ${c.text}`}>{card.value}</div>
                  <div className="text-xs font-bold text-gray-700 mt-1">{card.label}</div>
                  {card.sub && <div className="text-xs text-gray-400 mt-0.5">{card.sub}</div>}
                </div>
                <Icon name={card.icon} className={`h-6 w-6 shrink-0 ${c.text}`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Profitability */}
      {profitability && <ProfitabilitySection data={profitability} />}

      {/* Fulfillment Batches */}
      <div>
        <LogSectionHeader icon="calendar" title="Fulfillment Batches"
          subtitle="Subscription pick lists for the 1st and 15th — units and stock status per SKU" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <BatchCard batch={fulfillmentBatches.day1} />
          <BatchCard batch={fulfillmentBatches.day15} />
        </div>
      </div>

      {/* Best Sellers */}
      {bestSellers.length > 0 && <BestSellersSection sellers={bestSellers} />}

      {/* Subscription Matrix */}
      {subscriptionMatrix.length > 0 && <SubscriptionMatrix matrix={subscriptionMatrix} />}

      {/* Pending Shipments */}
      <PendingShipmentsTable shipments={pendingShipments} />

      {/* Inventory Alerts */}
      {inventoryAlerts.length > 0 && <InventoryAlertsSection alerts={inventoryAlerts} />}

    </div>
  );
}

// ─── Nav + Router ──────────────────────────────────────────────────────────────

const NAV_GROUPS = [
  { title: null, items: [
    { path: '/admin',               label: 'Overview',        icon: 'overview', exact: true },
  ]},
  { title: 'Sales', items: [
    { path: '/admin/orders',        label: 'Orders',          icon: 'orders' },
    { path: '/admin/subscriptions', label: 'Subscriptions',   icon: 'subscriptions' },
    { path: '/admin/members',       label: 'VIP Members',     icon: 'members' },
  ]},
  { title: 'Catalog', items: [
    { path: '/admin/products',      label: 'Products',        icon: 'products' },
    { path: '/admin/logistics',     label: 'Logistics',       icon: 'logistics' },
    { path: '/admin/forecast',      label: 'Profit Forecast', icon: 'forecast' },
    { path: '/admin/pricing',       label: 'Price Scheduler', icon: 'trend' },
  ]},
  { title: 'Growth', items: [
    { path: '/admin/marketing',     label: 'Marketing',       icon: 'marketing' },
    { path: '/admin/coupons',       label: 'Coupons',         icon: 'coupons' },
    { path: '/admin/analytics',     label: 'Analytics',       icon: 'analytics' },
  ]},
  { title: 'People', items: [
    { path: '/admin/users',         label: 'Users',           icon: 'users' },
    { path: '/admin/support',       label: 'Live Chat',       icon: 'chat' },
    { path: '/admin/tickets',       label: 'Tickets',         icon: 'ticket' },
  ]},
  { title: 'Payments', items: [
    { path: '/admin/card-vault',    label: 'Card Vault',      icon: 'cardVault' },
    { path: '/admin/crypto',        label: 'Crypto Wallets',  icon: 'crypto' },
    { path: '/admin/zelle',         label: 'Zelle Settings',  icon: 'zelle' },
  ]},
  { title: 'System', items: [
    { path: '/admin/coa',           label: 'COAs',            icon: 'coa' },
    { path: '/admin/media',         label: 'Media',           icon: 'media' },
    { path: '/admin/backup',        label: 'Backup',          icon: 'backup' },
    { path: '/admin/maintenance',   label: 'Maintenance',     icon: 'maintenance' },
  ]},
];

// Flat list — single source of truth for active-section lookups.
const NAV = NAV_GROUPS.flatMap(g => g.items);

// The four primary destinations pinned to the mobile bottom tab bar; everything
// else lives behind the "More" button, which opens the full grouped drawer.
const BOTTOM_NAV = [
  { path: '/admin',          label: 'Home',     icon: 'overview', exact: true },
  { path: '/admin/orders',   label: 'Orders',   icon: 'orders' },
  { path: '/admin/users',    label: 'Users',    icon: 'users' },
  { path: '/admin/products', label: 'Products', icon: 'products' },
];

function navItemActive(item, pathname) {
  return item.exact ? pathname === item.path : pathname.startsWith(item.path);
}

// Grouped link list, shared by the desktop sidebar and the mobile drawer.
function GroupedNavList({ pathname, isBlack, onNavigate }) {
  return (
    <>
      {NAV_GROUPS.map((group, gi) => (
        <div key={group.title || gi} className="space-y-1">
          {group.title && (
            <div className={'px-4 pt-1 text-[10px] font-bold uppercase tracking-wider ' +
              (isBlack ? 'text-gray-500' : 'text-gray-400')}>
              {group.title}
            </div>
          )}
          {group.items.map(n => {
            const active = navItemActive(n, pathname);
            return (
              <Link key={n.path} to={n.path} onClick={onNavigate}
                className={'flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors ' +
                  (active ? 'bg-tea-600 text-white'
                    : isBlack ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100')}>
                <Icon name={n.icon} className="h-5 w-5 shrink-0" />
                {n.label}
              </Link>
            );
          })}
        </div>
      ))}
    </>
  );
}

// Fixed bottom tab bar (mobile only): four primary tabs plus a "More" trigger.
function AdminBottomNav({ pathname, isBlack, onMore }) {
  return (
    <nav className={'md:hidden fixed bottom-0 inset-x-0 z-30 border-t flex shadow-[0_-1px_4px_rgba(0,0,0,0.08)] ' +
      (isBlack ? 'bg-black border-gray-800' : 'bg-white')}>
      {BOTTOM_NAV.map(n => {
        const active = navItemActive(n, pathname);
        return (
          <Link key={n.path} to={n.path}
            className={'flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[11px] font-medium ' +
              (active ? 'text-tea-600' : isBlack ? 'text-gray-400' : 'text-gray-500')}>
            <Icon name={n.icon} className="h-6 w-6" />
            {n.label}
          </Link>
        );
      })}
      <button onClick={onMore}
        className={'flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[11px] font-medium ' +
          (isBlack ? 'text-gray-400' : 'text-gray-500')}
        aria-label="More admin sections">
        <Icon name="menu" className="h-6 w-6" />
        More
      </button>
    </nav>
  );
}

// Slide-in drawer (mobile only) holding the full grouped navigation.
function AdminNavDrawer({ open, onClose, pathname, isBlack }) {
  return (
    <div className={'md:hidden fixed inset-0 z-50 ' + (open ? '' : 'pointer-events-none')}>
      <div onClick={onClose}
        className={'absolute inset-0 bg-black/40 transition-opacity duration-200 ' + (open ? 'opacity-100' : 'opacity-0')} />
      <div className={'absolute left-0 top-0 h-full w-72 max-w-[80vw] shadow-2xl flex flex-col transition-transform duration-200 ' +
        (open ? 'translate-x-0' : '-translate-x-full') + ' ' + (isBlack ? 'bg-black' : 'bg-white')}>
        <div className={'px-5 py-4 border-b flex items-center justify-between ' + (isBlack ? 'border-gray-800' : '')}>
          <span className={'font-bold ' + (isBlack ? 'text-gray-100' : 'text-gray-900')}>Admin menu</span>
          <button onClick={onClose} aria-label="Close menu"
            className={'text-2xl leading-none ' + (isBlack ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-700')}>×</button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-4">
          <GroupedNavList pathname={pathname} isBlack={isBlack} onNavigate={onClose} />
        </nav>
      </div>
    </div>
  );
}

// ─── Unique-visitor gong ──────────────────────────────────────────────────────
// Rings a gong for logged-in admins whenever a NEW unique visitor lands on the
// storefront. Polls the analytics arrivals feed (see routes/analytics.js); the
// gong is synthesised with the Web Audio API so there is no audio asset to ship.
// The on/off state is per-admin (localStorage) and the toggle click doubles as
// the user gesture that unlocks browser autoplay.
const GONG_LS_KEY = "tt_admin_gong_enabled";

function playGong(ctx, volume = 0.9) {
  const now = ctx.currentTime;

  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(volume, now + 0.012);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 3.6);

  // A slowly-closing low-pass gives the gong its decaying shimmer.
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(5200, now);
  lp.frequency.exponentialRampToValueAtTime(650, now + 3.4);
  lp.connect(master);
  master.connect(ctx.destination);

  // Inharmonic metallic partials = the bronze, gong-like timbre.
  const partials = [
    [82.41, 0.9], [110.0, 0.7], [164.8, 0.55], [220.0, 0.5], [277.2, 0.4],
    [329.6, 0.32], [440.0, 0.25], [587.3, 0.18], [739.9, 0.12],
  ];
  for (const [freq, g] of partials) {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq * (1 + (g - 0.5) * 0.004), now); // slight detune
    const og = ctx.createGain();
    og.gain.setValueAtTime(g, now);
    og.gain.exponentialRampToValueAtTime(0.0001, now + 2.4 + g);
    osc.connect(og); og.connect(lp);
    osc.start(now); osc.stop(now + 3.7);
  }

  // Short filtered noise burst = the mallet strike transient.
  const noiseLen = Math.floor(ctx.sampleRate * 0.18);
  const buf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < noiseLen; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / noiseLen);
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass"; bp.frequency.value = 1200;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.5 * volume, now);
  ng.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
  noise.connect(bp); bp.connect(ng); ng.connect(master);
  noise.start(now); noise.stop(now + 0.2);
}

function GongNotifier() {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(GONG_LS_KEY) === "1"; } catch { return false; }
  });
  const [flash, setFlash] = useState(null);
  const ctxRef = useRef(null);
  const sinceRef = useRef(null);
  const pollRef = useRef(null);
  const flashTimer = useRef(null);

  // Lazily create / resume the AudioContext (must follow a user gesture).
  const ensureAudio = () => {
    try {
      if (!ctxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctxRef.current = new AC();
      }
      if (ctxRef.current.state === "suspended") ctxRef.current.resume().catch(() => {});
      return ctxRef.current;
    } catch { return null; }
  };

  // If the gong was left ON from a previous session, the browser auto-suspends
  // audio until a gesture — re-unlock on the first interaction anywhere.
  useEffect(() => {
    if (!enabled) return undefined;
    const unlock = () => ensureAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [enabled]);

  // Poll the arrivals feed while enabled.
  useEffect(() => {
    if (!enabled) {
      if (pollRef.current) clearInterval(pollRef.current);
      sinceRef.current = null;
      return undefined;
    }
    let stopped = false;
    const poll = async () => {
      try {
        const params = sinceRef.current != null ? { since: sinceRef.current } : {};
        const { data } = await api.get("/admin/analytics/arrivals", { params });
        if (stopped) return;
        const firstSync = sinceRef.current == null;
        sinceRef.current = data.seq;
        if (firstSync) return; // sync only — do not gong for the backlog
        const arr = data.arrivals || [];
        if (arr.length) {
          const ctx = ensureAudio();
          if (ctx) playGong(ctx);
          const a = arr[arr.length - 1];
          const place = [a.city, a.region, a.country].filter(Boolean).join(", ");
          const label = arr.length > 1
            ? arr.length + " new visitors just landed"
            : (a.isNewVisitor ? "New visitor" : "Visitor") + (place ? " · " + place : "");
          setFlash({ label, at: Date.now() });
          if (flashTimer.current) clearTimeout(flashTimer.current);
          flashTimer.current = setTimeout(() => setFlash(null), 6000);
        }
      } catch { /* ignore transient errors (401 on logout, network blips) */ }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => { stopped = true; clearInterval(pollRef.current); };
  }, [enabled]);

  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  const toggle = () => {
    setEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem(GONG_LS_KEY, next ? "1" : "0"); } catch {}
      if (next) {
        const ctx = ensureAudio();
        if (ctx) playGong(ctx, 0.45); // soft confirmation tap so they know it works
      }
      return next;
    });
  };

  return (
    <>
      {flash && (
        <div className="fixed left-4 bottom-32 md:bottom-20 z-40">
          <div className="flex items-center gap-2 rounded-full bg-amber-500 text-white shadow-lg px-4 py-2 text-sm font-semibold animate-bounce">
            <Icon name="bell" className="h-5 w-5" />
            {flash.label}
          </div>
        </div>
      )}
      <button onClick={toggle}
        title={enabled ? "Visitor gong is ON — click to mute" : "Visitor gong is OFF — click to enable"}
        aria-label={enabled ? "Mute new-visitor gong" : "Enable new-visitor gong"}
        className={"fixed left-4 bottom-20 md:bottom-6 z-40 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-lg transition-colors " +
          (enabled ? "bg-tea-600 text-white hover:bg-tea-700" : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-50")}>
        <Icon name={enabled ? 'bell' : 'bellOff'} className="h-5 w-5" />
        <span className="hidden sm:inline">Gong {enabled ? "On" : "Off"}</span>
      </button>
    </>
  );
}

export default function AdminPage() {
  const location = useLocation();
  const { user } = useAuthCtx();
  const isBlack = user?.email === 'black@alohaamino.com';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const activeName = NAV.find(n => navItemActive(n, location.pathname))?.label || 'Admin';

  // Close the mobile drawer whenever navigation moves to a new section.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Mark admin "online" so customers waiting on a payment confirmation see
  // the green dot in the chat. Heartbeat fires immediately + every 30s.
  useEffect(() => {
    const ping = () => api.post('/admin/heartbeat').catch(() => {});
    ping();
    const id = setInterval(ping, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={`min-h-screen flex flex-col ${isBlack ? 'bg-black' : 'bg-gray-50'}`}>
      <Navbar />
      {/* Mobile top bar: current section + drawer trigger */}
      <div className={'md:hidden sticky top-0 z-30 border-b px-4 py-3 flex items-center gap-2 ' +
        (isBlack ? 'bg-black border-gray-800' : 'bg-white')}>
        <button onClick={() => setDrawerOpen(true)}
          className={'flex items-center gap-2 font-semibold ' + (isBlack ? 'text-gray-100' : 'text-gray-800')}
          aria-label="Open admin menu">
          <Icon name="menu" className="h-6 w-6" />
          <span>{activeName}</span>
        </button>
      </div>

      {/* Mobile slide-in drawer with the full grouped nav */}
      <AdminNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
        pathname={location.pathname} isBlack={isBlack} />

      <div className="flex flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 gap-8 w-full">
        {/* Desktop sidebar (grouped) */}
        <aside className="w-48 flex-shrink-0 hidden md:block">
          <nav className="space-y-4">
            <GroupedNavList pathname={location.pathname} isBlack={isBlack} />
          </nav>
        </aside>

        {/* Main content area */}
        <main id="main-content" tabIndex={-1} className="flex-1 min-w-0 pb-24 md:pb-0">
          <Routes>
            <Route index                   element={<BoundedRoute element={<MetricsOverview />} />} />
            <Route path="products"         element={<BoundedRoute element={<ProductsList />} />} />
            <Route path="orders"           element={<BoundedRoute element={<OrdersList />} />} />
            <Route path="subscriptions"    element={<BoundedRoute element={<SubscriptionsList />} />} />
            <Route path="members"          element={<BoundedRoute element={<MembersList />} />} />
            <Route path="card-vault"       element={<BoundedRoute element={<CardVault />} />} />
            <Route path="logistics"        element={<BoundedRoute element={<LogisticsCenter />} />} />
            <Route path="users"            element={<BoundedRoute element={<UsersList />} />} />
            <Route path="support"          element={<BoundedRoute element={<SupportInbox />} />} />
            <Route path="tickets"          element={<BoundedRoute element={<TicketsPage />} />} />
            <Route path="analytics"        element={<BoundedRoute element={<AnalyticsPage />} />} />
            <Route path="marketing"        element={<BoundedRoute element={<MarketingPage />} />} />
            <Route path="coupons"          element={<BoundedRoute element={<CouponsPage />} />} />
            <Route path="crypto"           element={<BoundedRoute element={<CryptoSettings />} />} />
            <Route path="zelle"            element={<BoundedRoute element={<ZelleSettings />} />} />
            <Route path="coa"              element={<BoundedRoute element={<COAManager />} />} />
            <Route path="media"            element={<BoundedRoute element={<MediaLibrary />} />} />
            <Route path="backup"           element={<BoundedRoute element={<BackupExport />} />} />
            <Route path="forecast"         element={<BoundedRoute element={<ForecastPage />} />} />
            <Route path="pricing"          element={<BoundedRoute element={<PriceSchedulerPage />} />} />
            <Route path="maintenance"      element={<BoundedRoute element={<MaintenanceSettings />} />} />
          </Routes>
        </main>
      </div>

      {/* Mobile bottom tab bar: primary destinations + More */}
      <AdminBottomNav pathname={location.pathname} isBlack={isBlack}
        onMore={() => setDrawerOpen(true)} />

      <AdminChatDock />
      <GongNotifier />
    </div>
  );
}
